// src/components/RaiseInspectionCallForm.js
// Raising an Inspection Call Form - ERC Raw Material, Process, Final stages
// Based on SARTHI specification for Indian Railways inspection management

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  // COMPANY_UNIT_MASTER, // Commented out - now using POI API instead of mock data
  // MANUFACTURER_MASTER,
  HEAT_TC_MAPPING,
  CHEMICAL_ANALYSIS_HISTORY,
  RM_INSPECTION_CALLS,
  // PROCESS_INSPECTION_CALLS, // Removed unused import
  // LOT_NUMBERS, // Removed unused import
  // ERC_CONVERSION_FACTORS, // Removed - using direct calculation instead
  PO_SERIAL_DETAILS,
  // VENDOR_INVENTORY_ENTRIES, // Import inventory data
  // VENDOR_PO_LIST
} from '../data/vendorMockData';
import inspectionCallService from '../services/inspectionCallService';
import poiMappingService from '../services/poiMappingService';
import HeatSummaryTable from './HeatSummaryTable';
import Notification from './Notification';
import '../styles/raiseInspectionCall.css';

// Multi-Select Dropdown Component
const MultiSelectDropdown = ({ options = [], selectedValues = [], onChange, placeholder, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);

  const currentSelected = useMemo(() => {
    return Array.isArray(selectedValues) ? selectedValues : [];
  }, [selectedValues]);

  const toggleDropdown = () => {
    if (!disabled) setIsOpen(!isOpen);
  };

  const isOptionSelected = (optVal) => {
    if (optVal === undefined || optVal === null) return false;
    return currentSelected.some(sv => {
      if (sv === undefined || sv === null) return false;
      if (sv === optVal) return true;
      const s1 = String(sv).trim();
      const s2 = String(optVal).trim();
      if (s1 === s2) return true;
      const m1 = s1.match(/[^/]+\/([^/]+)\//);
      const ic1 = m1 ? m1[1] : s1;
      const m2 = s2.match(/[^/]+\/([^/]+)\//);
      const ic2 = m2 ? m2[1] : s2;
      return ic1 && ic2 && ic1.toUpperCase() === ic2.toUpperCase();
    });
  };

  const handleOptionClick = (value) => {
    let newValues;
    if (isOptionSelected(value)) {
      newValues = currentSelected.filter(sv => {
        if (sv === value) return false;
        const s1 = String(sv).trim();
        const s2 = String(value).trim();
        if (s1 === s2) return false;
        const m1 = s1.match(/[^/]+\/([^/]+)\//);
        const ic1 = m1 ? m1[1] : s1;
        const m2 = s2.match(/[^/]+\/([^/]+)\//);
        const ic2 = m2 ? m2[1] : s2;
        return !(ic1 && ic2 && ic1.toUpperCase() === ic2.toUpperCase());
      });
    } else {
      newValues = [...currentSelected, value];
    }
    onChange(newValues);
  };

  const getDisplayText = () => {
    if (currentSelected.length === 0) return placeholder;
    if (currentSelected.length === 1) return currentSelected[0];
    return `${currentSelected.length} items selected`;
  };

  // Merge options with any selectedValues not present in options (e.g. during initial modify loading)
  const displayOptions = useMemo(() => {
    const list = Array.isArray(options) ? [...options] : [];
    const hasMatch = (val) => list.some(o => {
      if (o.value === val) return true;
      const s1 = String(o.value || '').trim();
      const s2 = String(val || '').trim();
      if (s1 === s2) return true;
      const m1 = s1.match(/[^/]+\/([^/]+)\//);
      const ic1 = m1 ? m1[1] : s1;
      const m2 = s2.match(/[^/]+\/([^/]+)\//);
      const ic2 = m2 ? m2[1] : s2;
      return ic1 && ic2 && ic1.toUpperCase() === ic2.toUpperCase();
    });

    currentSelected.forEach(sv => {
      if (sv && !hasMatch(sv)) {
        list.push({
          value: sv,
          label: sv
        });
      }
    });
    return list;
  }, [options, currentSelected]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.ric-multiselect-dropdown')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div
      className="ric-multiselect-dropdown"
      style={disabled ? { pointerEvents: 'none', opacity: 0.7 } : {}}
    >
      <div
        className="ric-multiselect-trigger"
        onClick={toggleDropdown}
        style={disabled ? { cursor: 'not-allowed', backgroundColor: '#f3f4f6' } : {}}
      >
        <span>{getDisplayText()}</span>
        <span className="ric-multiselect-arrow">{isOpen ? '▲' : '▼'}</span>
      </div>
      {isOpen && (
        <div className="ric-multiselect-options">
          {displayOptions.length === 0 ? (
            <div style={{ padding: '12px', color: '#6b7280', textAlign: 'center' }}>
              No options available
            </div>
          ) : (
            displayOptions.map((option) => (
              <div
                key={option.value}
                className={`ric-multiselect-option ${isOptionSelected(option.value) ? 'ric-multiselect-option--selected' : ''}`}
                onClick={() => handleOptionClick(option.value)}
              >
                <input
                  type="checkbox"
                  checked={isOptionSelected(option.value)}
                  onChange={() => { }}
                  className="ric-multiselect-checkbox"
                />
                <span>{option.label}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================
// MOCK DATA FOR TESTING PROCESS IC (REMOVED - NOW USING REAL API)
// ============================================================
// Mock data removed as we're now fetching real data from the backend API

// Mock RM IC data
// const MOCK_RM_INSPECTION_CALLS = [
//   {
//     icNumber: 'RMIC-001',
//     poSerialNo: 'PO-SR-001',
//     heatNumber: 'H-101',
//     qtyAccepted: 12.5
//   },
//   {
//     icNumber: 'RMIC-002',
//     poSerialNo: 'PO-SR-001',
//     heatNumber: 'H-102',
//     qtyAccepted: 10.0
//   },
//   {
//     icNumber: 'RMIC-003',
//     poSerialNo: 'PO-SR-002',
//     heatNumber: 'H-201',
//     qtyAccepted: 8.75
//   },
//   {
//     icNumber: 'RMIC-004',
//     poSerialNo: 'PO-SR-001',
//     heatNumber: 'H-103',
//     qtyAccepted: 15.0
//   }
// ];


// Helper functions
const getTodayDate = () => new Date().toISOString().split('T')[0];
const getMaxDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 6);
  return date.toISOString().split('T')[0];
};
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Inspection stage options
const INSPECTION_STAGES = [
  { value: '', label: 'Select Type of Call' },
  { value: 'Raw Material', label: ' Raw Material' },
  { value: 'Process', label: ' Process' },
  { value: 'Final', label: ' Final' }
];

// Call Nature options
const CALL_NATURE_OPTIONS = [
  { value: 'FRESH', label: 'Fresh Call' },
  { value: 'DIVERTED', label: 'Diverted Call' }
];

// ERC type options
const ERC_TYPES = [
  { value: '', label: 'Select Type of ERC' },
  { value: 'MK-III', label: 'MK-III' },
  { value: 'MK-V', label: 'MK-V' },
  { value: 'J-Type', label: 'J-Type' }
];

// Form field component - MOVED OUTSIDE to prevent re-creation on every render
const FormField = ({ label, name, required, hint, children, fullWidth = false, errors = {} }) => {
  const hasError = errors[name];

  // Clone children and add error class if there's an error
  const childrenWithError = hasError && children?.props?.className
    ? {
      ...children,
      props: {
        ...children.props,
        className: `${children.props.className} error`
      }
    }
    : children;

  return (
    <div className={`ric-form-group ${fullWidth ? 'ric-form-group--full-width' : ''} ${hasError ? 'has-error' : ''}`}>
      <label className="ric-form-label">
        {label} {required && <span className="ric-required">*</span>}
      </label>
      {childrenWithError}
      {hint && !hasError && <span className="ric-form-hint">{hint}</span>}
      {hasError && <span className="ric-form-error">⚠️ {errors[name]}</span>}
    </div>
  );
};

// Section header component - MOVED OUTSIDE to prevent re-creation on every render
const SectionHeader = ({ title, subtitle }) => (
  <div className="ric-section-header">
    <h4 className="ric-section-title">{title}</h4>
    {subtitle && <p className="ric-section-subtitle">{subtitle}</p>}
  </div>
);

// Initial form state generator
const getInitialFormState = (selectedPO = null, selectedItem = null) => {
  // Find the first PO serial for the selected PO
  const poSerial = selectedPO ? PO_SERIAL_DETAILS.find(p => p.poNo === selectedPO.po_no) : null;

  // Base defaults
  const defaults = {
    // === COMMON SECTION (Auto Fetched from IREPS) ===
    po_no: selectedPO?.po_no || '',
    zone_name: selectedPO?.zone_name || selectedPO?.rlyShortName || 'N/A',
    po_serial_no: '', // Will be set when user selects from dropdown
    po_date: selectedPO?.po_date || '',
    po_description: selectedPO?.description || '',
    po_qty: 0, // Will be set when user selects PO serial
    po_unit: selectedPO?.unit || '',
    amendment_no: '',
    amendment_date: '',
    vendor_contact_name: '',
    vendor_contact_phone: '',

    // === CALL TYPE ===
    call_category: selectedItem?.call_category || selectedItem?.callCategory || 'FRESH',
    type_of_call: '',
    type_of_erc: '',
    desired_inspection_date: '',

    // === QUANTITY TRACKING (Auto Calculated) ===
    qty_already_inspected_rm: selectedItem?.qty_already_inspected_rm || poSerial?.qtyAlreadyInspected?.rm || 0,
    qty_already_inspected_process: selectedItem?.qty_already_inspected_process || poSerial?.qtyAlreadyInspected?.process || 0,
    qty_already_inspected_final: selectedItem?.qty_already_inspected_final || poSerial?.qtyAlreadyInspected?.final || 0,

    // === RAW MATERIAL STAGE FIELDS ===
    // NEW: Array of heat-TC mappings with auto-fetched details
    rm_heat_tc_mapping: [
      {
        id: Date.now(), // Unique ID for React key
        heatNumber: '',
        supplierName: '',
        compositeKey: '', // Composite key: "heatNumber|supplierName" to uniquely identify heat
        tcNumber: '',
        tcDate: '',
        manufacturer: '',
        invoiceNo: '',
        invoiceDate: '',
        subPoNumber: '',
        subPoDate: '',
        subPoQty: '',
        subPoTotalValue: '',
        tcQty: '',
        tcQtyRemaining: '',
        offeredQty: '',
        maxQty: '',
        unit: '',
        isLoading: false,
        isLoadingChemical: false, // Loading state for chemical analysis auto-fetch
        chemicalAutoFetched: false, // Flag to indicate if chemical data was auto-fetched
        chemicalReadOnly: false, // Flag to indicate if chemical data should be read-only
        // Chemical Analysis - Now per heat number
        chemical_carbon: '',
        chemical_manganese: '',
        chemical_silicon: '',
        chemical_sulphur: '',
        chemical_phosphorus: ''
      }
    ],
    rm_total_offered_qty_mt: 0, // Auto-calculated (sum of all heat quantities)
    rm_offered_qty_erc: 0, // Auto-calculated from total

    // === PROCESS STAGE FIELDS ===
    process_rm_ic_numbers: [], // Multi-select from completed RM ICs
    process_book_set_nos: [], // Auto-fetched based on selected RM ICs (array of {icNumber, bookSetNo})
    // NEW: Array of lot-heat mappings
    process_lot_heat_mapping: [
      {
        id: Date.now(),
        lotNumber: '',
        manufacturerHeat: '',
        heatNumber: '',
        manufacturer: '',
        compositeKey: '', // Composite key: "heatNumber|manufacturer" to uniquely identify heat
        offeredQty: '',
        totalAcceptedQtyRm: 0,
        tentativeStartDate: '', // Tentative date of start of production
        isLoading: false
      }
    ],
    process_total_accepted_qty_rm: 0, // Total accepted quantity from selected RM ICs

    // === FINAL STAGE FIELDS ===
    final_lot_numbers: [], // Multi-select dropdown - Lots from Process ICs
    final_selected_lot: '', // Currently selected lot in dropdown (before adding)
    final_manufacturer_heat: '', // Auto-fetched based on selected lots
    final_erc_qty: '', // Free text, integer - Quantity (No. of ERC)
    final_total_qty: '', // Auto-calculated: final_erc_qty * final_hdpe_bags
    final_hdpe_bags: '', // Free text, integer
    final_rm_ic_numbers: [], // Multi-select dropdown
    final_process_ic_numbers: [], // Multi-select dropdown
    final_total_accepted_qty_process: 0, // Total accepted quantity from selected Process ICs
    final_unit_id: '', // Auto-filled from Process IC
    final_unit_name: '', // Auto-filled from Process IC
    final_unit_address: '', // Auto-filled from Process IC
    final_lots_data: [], // Array of { lotNumber, heatNo, acceptedQtyProcess, offeredQty, noOfBags }

    // === PLACE OF INSPECTION ===
    company_id: '', // User selects from dropdown
    company_name: '', // Auto-filled based on company_id selection
    cin: '', // Auto-filled from master data
    unit_id: '',
    unit_name: '',
    unit_address: '',
    unit_gstin: '',
    unit_contact_person: '',
    unit_role: '',

    // === ADDITIONAL ===
    remarks: ''
  };

  // If selectedItem carries prefilled data (e.g. in modify mode), spread it over the defaults.
  // This lets VendorDashboardPage pass all IC fields through selectedItem.
  if (selectedItem && selectedItem.type_of_call) {
    const initialState = { ...defaults, ...selectedItem };

    // In modify mode, the backend's maxQty/tcQtyRemaining for Raw Material already has the 
    // original offered quantity deducted. We need to add it back for validation.
    if (initialState.rm_heat_tc_mapping && Array.isArray(initialState.rm_heat_tc_mapping)) {
      initialState.rm_heat_tc_mapping = initialState.rm_heat_tc_mapping.map(heat => {
        if (heat.offeredQty) {
          const offered = parseFloat(heat.offeredQty) || 0;
          const currentMax = parseFloat(heat.maxQty) || parseFloat(heat.tcQtyRemaining) || 0;
          const newMax = currentMax + offered;
          const unit = heat.unit || (heat.tcQtyRemaining && heat.tcQtyRemaining.includes('MT') ? 'MT' : 'Nos');

          return {
            ...heat,
            maxQty: newMax,
            tcQtyRemaining: `${newMax} ${unit}`
          };
        }
        return heat;
      });
    }

    return initialState;
  }

  return defaults;
};

export const RaiseInspectionCallForm = ({
  inventoryEntries = [],
  availableHeatNumbers = [],
  selectedPO = null,
  selectedItem = null,
  selectedSubPO = null,
  vendorId = null,
  onSubmit,
  isLoading = false,
  isModifyMode = false,
  isViewMode = false,
  blockedCallTypes = new Set(),
  completeDiversionSrNos = new Set()
}) => {
  const [formData, setFormData] = useState(() => getInitialFormState(selectedPO, selectedItem));
  const [errors, setErrors] = useState({});
  const [selectedPoSerial, setSelectedPoSerial] = useState(selectedItem?.po_serial_no || '');
  const [showErcWarning, setShowErcWarning] = useState(false);
  const [pendingErcType, setPendingErcType] = useState('');

  // State for approved RM ICs and heat numbers (for Process IC)
  // eslint-disable-next-line no-unused-vars
  const [approvedRMICsForProcess, setApprovedRMICsForProcess] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [processHeatNumbers, setProcessHeatNumbers] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [loadingRMICs, setLoadingRMICs] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [loadingHeats, setLoadingHeats] = useState(false);

  // Final Inspection Call dropdown states
  const [processIcCertificates, setProcessIcCertificates] = useState([]);
  const [rmIcNumbersForFinal, setRmIcNumbersForFinal] = useState([]);
  const [lotNumbersForFinal, setLotNumbersForFinal] = useState([]);
  const [loadingProcessIcs, setLoadingProcessIcs] = useState(false);
  const [loadingRmIcsForFinal, setLoadingRmIcsForFinal] = useState(false);
  const [loadingLotsForFinal, setLoadingLotsForFinal] = useState(false);

  // State to store lot-heat mapping for Final Inspection
  const [lotHeatMapping, setLotHeatMapping] = useState({});

  // POI (Place of Inspection) states
  const [companies, setCompanies] = useState([]);
  const [units, setUnits] = useState([]);
  const [poiCode, setPoiCode] = useState(selectedItem?.placeOfInspection || selectedItem?.place_of_inspection || '');
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [loadingUnitDetails, setLoadingUnitDetails] = useState(false);
  const [isPoiBlocked, setIsPoiBlocked] = useState(false);
  const [poiBlockMessage, setPoiBlockMessage] = useState('');

  // Notification state
  const [notification, setNotification] = useState({ message: '', type: 'error' });

  // Heat Summary states
  const [heatSummaryData, setHeatSummaryData] = useState([]);
  const [loadingHeatSummary, setLoadingHeatSummary] = useState(false);

  // Handle PO Serial selection
  const handlePoSerialChange = useCallback((serialNo, itemData = null) => {
    const poSerial = PO_SERIAL_DETAILS.find(p => p.serialNo === serialNo);
    if (poSerial) {
      // Use mock data if found
      setSelectedPoSerial(serialNo);
      setFormData(prev => ({
        ...prev,
        po_serial_no: serialNo,
        po_no: poSerial.poNo,
        po_qty: poSerial.poQty,
        po_unit: poSerial.unit,
        amendment_no: poSerial.amendmentNo || '',
        amendment_date: poSerial.amendmentDate || '',
        qty_already_inspected_rm: poSerial.qtyAlreadyInspected.rm,
        qty_already_inspected_process: poSerial.qtyAlreadyInspected.process,
        qty_already_inspected_final: poSerial.qtyAlreadyInspected.final
      }));
    } else if (itemData) {
      // Use real data from API if mock data not found
      setSelectedPoSerial(serialNo);
      setFormData(prev => ({
        ...prev,
        po_serial_no: serialNo,
        po_qty: itemData.item_qty || 0,
        po_unit: itemData.item_unit || '',
        qty_already_inspected_rm: itemData.qty_already_inspected_rm || 0,
        qty_already_inspected_process: itemData.qty_already_inspected_process || 0,
        qty_already_inspected_final: itemData.qty_already_inspected_final || 0
      }));
    }
  }, []);

  // Reset form when PO or Item changes
  useEffect(() => {
    setFormData(getInitialFormState(selectedPO, selectedItem));
    setErrors({});

    // Auto-fill PO Serial Number if item is provided
    if (selectedItem?.po_serial_no) {
      setSelectedPoSerial(selectedItem.po_serial_no);
      if (!isModifyMode) {
        handlePoSerialChange(selectedItem.po_serial_no, selectedItem);
      }
    }
  }, [selectedPO, selectedItem, handlePoSerialChange, isModifyMode]);

  // Pre-fill inventory and chemical details in modify mode when inventoryEntries are loaded
  useEffect(() => {
    if (!isModifyMode || !formData.rm_heat_tc_mapping || formData.rm_heat_tc_mapping.length === 0) {
      return;
    }

    let isUpdated = false;

    // 1. Resolve inventory details (Sub PO Total Value, TC Qty, TC Qty Remaining with Vendor)
    const resolvedMapping = formData.rm_heat_tc_mapping.map(heat => {
      // Check if we need to resolve inventory details
      if (heat.heatNumber && heat.tcNumber && (!heat.subPoTotalValue || heat.tcQtyRemaining === 'null' || heat.tcQtyRemaining === '')) {
        const entry = inventoryEntries.find(
          e => e.heatNumber === heat.heatNumber && e.tcNumber === heat.tcNumber
        );
        if (entry) {
          const totalValue = (
            entry.subPoQty *
            entry.rateOfMaterial *
            (1 + entry.rateOfGst / 100)
          ).toFixed(2);

          let currentQtyLeft = parseFloat(entry.qtyLeftForInspection) || 0;

          if (isModifyMode && selectedItem && selectedItem.rm_heat_tc_mapping) {
            const originalHeat = selectedItem.rm_heat_tc_mapping.find(h => h.heatNumber === heat.heatNumber && h.tcNumber === heat.tcNumber);
            if (originalHeat && originalHeat.offeredQty) {
              currentQtyLeft += parseFloat(originalHeat.offeredQty) || 0;
            }
          }

          isUpdated = true;
          return {
            ...heat,
            tcDate: entry.tcDate || heat.tcDate || '',
            manufacturer: entry.supplierName || heat.manufacturer || '',
            invoiceNo: entry.invoiceNumber || heat.invoiceNo || '',
            invoiceDate: entry.invoiceDate || heat.invoiceDate || '',
            subPoNumber: entry.subPoNumber || heat.subPoNumber || '',
            subPoDate: entry.subPoDate || heat.subPoDate || '',
            subPoQty: `${entry.subPoQty} ${entry.unitOfMeasurement}`,
            subPoTotalValue: `₹${totalValue}`,
            tcQty: `${entry.tcQuantity} ${entry.unitOfMeasurement}`,
            tcQtyRemaining: `${currentQtyLeft} ${entry.unitOfMeasurement}`,
            maxQty: currentQtyLeft,
            unit: entry.unitOfMeasurement
          };
        }
      }
      return heat;
    });

    if (isUpdated) {
      setFormData(prev => ({
        ...prev,
        rm_heat_tc_mapping: resolvedMapping
      }));
    }
  }, [isModifyMode, inventoryEntries, formData.rm_heat_tc_mapping, selectedItem]);

  // Auto-fetch chemical analysis in modify mode if chemical fields are empty
  useEffect(() => {
    if (!isModifyMode || !formData.rm_heat_tc_mapping || formData.rm_heat_tc_mapping.length === 0) {
      return;
    }

    formData.rm_heat_tc_mapping.forEach(async (heat) => {
      if (heat.heatNumber && !heat.chemical_carbon && !heat.isLoadingChemical && !heat.chemicalFetchedInModify) {
        // Mark as loading and set a flag to prevent duplicate fetches
        setFormData(prev => ({
          ...prev,
          rm_heat_tc_mapping: prev.rm_heat_tc_mapping.map(h =>
            h.id === heat.id ? { ...h, isLoadingChemical: true, chemicalFetchedInModify: true } : h
          )
        }));

        try {
          console.log(`🔬 Auto-fetching chemical analysis in modify mode for heat number: ${heat.heatNumber}`);
          const response = await inspectionCallService.getChemicalAnalysisByHeatNumber(heat.heatNumber);
          if (response && response.data) {
            const analysis = response.data;
            console.log('✅ Chemical analysis fetched in modify mode:', analysis);
            setFormData(prev => ({
              ...prev,
              rm_heat_tc_mapping: prev.rm_heat_tc_mapping.map(h =>
                h.id === heat.id
                  ? {
                    ...h,
                    chemical_carbon: analysis.carbon || '',
                    chemical_manganese: analysis.manganese || '',
                    chemical_silicon: analysis.silicon || '',
                    chemical_sulphur: analysis.sulphur || '',
                    chemical_phosphorus: analysis.phosphorus || '',
                    isLoadingChemical: false,
                    chemicalAutoFetched: true,
                    chemicalReadOnly: false
                  }
                  : h
              )
            }));
          } else {
            setFormData(prev => ({
              ...prev,
              rm_heat_tc_mapping: prev.rm_heat_tc_mapping.map(h =>
                h.id === heat.id ? { ...h, isLoadingChemical: false } : h
              )
            }));
          }
        } catch (error) {
          console.error('❌ Error fetching chemical analysis in modify mode:', error);
          setFormData(prev => ({
            ...prev,
            rm_heat_tc_mapping: prev.rm_heat_tc_mapping.map(h =>
              h.id === heat.id ? { ...h, isLoadingChemical: false } : h
            )
          }));
        }
      }
    });
  }, [isModifyMode, formData.rm_heat_tc_mapping]);

  // Auto-fetch heat summary in modify mode when process_rm_ic_numbers is pre-populated
  const [heatSummaryFetchedInModify, setHeatSummaryFetchedInModify] = useState(false);
  useEffect(() => {
    if (
      isModifyMode &&
      formData.type_of_call === 'Process' &&
      formData.process_rm_ic_numbers &&
      formData.process_rm_ic_numbers.length > 0 &&
      !heatSummaryFetchedInModify
    ) {
      console.log('📊 [Modify Mode] Auto-fetching heat summary for pre-filled RM ICs:', formData.process_rm_ic_numbers);
      setHeatSummaryFetchedInModify(true);
      fetchHeatSummaryData(formData.process_rm_ic_numbers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModifyMode, formData.type_of_call, formData.process_rm_ic_numbers, heatSummaryFetchedInModify]);

  // Get available PO serial numbers for dropdown
  const poSerialOptions = useMemo(() => {
    if (!selectedPO) return PO_SERIAL_DETAILS;
    return PO_SERIAL_DETAILS.filter(p => p.poNo === selectedPO.po_no);
  }, [selectedPO]);

  // Get available heat numbers from inventory
  // const availableHeatNumbers = useMemo(() => {
  //   return HEAT_TC_MAPPING.filter(h => h.qtyAvailable > 0);
  // }, []);

  // Fetch completed RM ICs (certificate numbers) when call type is Process
  useEffect(() => {
    const fetchCompletedRMICs = async () => {
      if (formData.type_of_call === 'Process') {
        setLoadingRMICs(true);

        try {
          console.log('🔍 Fetching completed RM ICs (certificate numbers) for PO Serial No:', formData.po_serial_no, 'call_category:', formData.call_category);
          const response = await inspectionCallService.getCompletedRmIcNumbers(formData.po_serial_no, formData.call_category);
          console.log('📦 Completed certificate numbers response:', response);

          if (response && response.data) {
            // Response.data is an array of certificate numbers (e.g., "N/ER-01080001/RAJK")
            const certificateNumbers = Array.isArray(response.data) ? response.data : [];
            console.log('✅ Setting completed certificate numbers:', certificateNumbers);

            // Convert to the format expected by the dropdown
            // Extract call number from certificate number for API calls
            // Certificate format: "N/ER-01080001/RAJK" → Call number: "ER-01080001"
            const formattedData = certificateNumbers.map(item => {
              const certNo = (item && typeof item === 'object') ? item.certificateNo : (item || '');
              const createdAt = (item && typeof item === 'object') ? item.createdAt : null;

              // Extract call number from certificate number
              // Pattern: N/{CALL_NO}/{SUFFIX} → extract {CALL_NO}
              const callNoMatch = certNo ? certNo.match(/N\/([^/]+)\//) : null;
              const callNo = callNoMatch ? callNoMatch[1] : certNo;
              const displayLabel = createdAt ? `${certNo} (${formatDate(createdAt)})` : certNo;

              return {
                certificate_no: certNo,  // Display value (e.g., "N/ER-01080001/RAJK")
                ic_number: callNo,       // API value (e.g., "ER-01080001")
                createdAt: createdAt,
                label: displayLabel      // Dropdown label (show certificate number with date)
              };
            });

            // In modify mode: ensure any pre-selected RM IC numbers that are NOT in the
            // fetched list are still available as options so they show as checked.
            if (isModifyMode && formData.process_rm_ic_numbers && formData.process_rm_ic_numbers.length > 0) {
              const existingValues = new Set(formattedData.map(d => d.certificate_no));
              formData.process_rm_ic_numbers.forEach(preSelected => {
                if (!existingValues.has(preSelected)) {
                  // Derive call number from this value (same pattern as above)
                  const callNoMatch = preSelected.match(/[^/]+\/([^/]+)\//);
                  const callNo = callNoMatch ? callNoMatch[1] : preSelected;
                  formattedData.push({
                    certificate_no: preSelected,
                    ic_number: callNo,
                    label: preSelected
                  });
                }
              });
            }

            // Hide ICs whose PO SR no has a COMPLETE diversion (unless it is a diverted call)
            const visibleData = (formData.call_category !== 'DIVERTED' && completeDiversionSrNos.has(formData.po_serial_no))
              ? []
              : formattedData;
            setApprovedRMICsForProcess(visibleData);
          } else {
            console.log('⚠️ Response not successful, setting empty array');
            // Still inject pre-selected values so they show in modify mode
            if (isModifyMode && formData.process_rm_ic_numbers && formData.process_rm_ic_numbers.length > 0) {
              const fallback = formData.process_rm_ic_numbers.map(preSelected => {
                const callNoMatch = preSelected.match(/[^/]+\/([^/]+)\//);
                const callNo = callNoMatch ? callNoMatch[1] : preSelected;
                return { certificate_no: preSelected, ic_number: callNo, label: preSelected };
              });
              setApprovedRMICsForProcess(fallback);
            } else {
              setApprovedRMICsForProcess([]);
            }
          }
        } catch (error) {
          console.error('❌ Error fetching completed certificate numbers:', error);
          setApprovedRMICsForProcess([]);
        } finally {
          setLoadingRMICs(false);
        }
      } else {
        // Reset when not Process type
        setApprovedRMICsForProcess([]);
      }
    };

    fetchCompletedRMICs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.type_of_call, formData.po_serial_no, formData.call_category]);

  // Fetch companies for POI dropdown on component mount
  useEffect(() => {
    const fetchCompanies = async () => {
      if (!vendorId) {
        console.warn('⚠️ No vendorId provided, skipping company fetch');
        return;
      }
      setLoadingCompanies(true);
      try {
        console.log('🏢 Fetching companies for POI dropdown for vendor:', vendorId);
        const response = await poiMappingService.getCompanies(vendorId);
        console.log('📦 Companies response:', response);

        if (response && response.data) {
          const companyList = Array.isArray(response.data) ? response.data : [];
          console.log('✅ Setting companies:', companyList);
          setCompanies(companyList);
        } else {
          console.log('⚠️ Response not successful, setting empty array');
          setCompanies([]);
        }
      } catch (error) {
        console.error('❌ Error fetching companies:', error);
        setCompanies([]);
      } finally {
        setLoadingCompanies(false);
      }
    };

    fetchCompanies();
  }, [vendorId]); // Re-fetch if vendorId changes

  // Fetch units when company is selected
  useEffect(() => {
    const fetchUnits = async () => {
      if (formData.company_name) {
        setLoadingUnits(true);
        try {
          console.log('🏭 Fetching units for company:', formData.company_name);
          const response = await poiMappingService.getUnitsByCompany(formData.company_name);
          console.log('📦 Units response:', response);

          if (response && response.data) {
            const unitList = Array.isArray(response.data) ? response.data : [];
            console.log('✅ Setting units:', unitList);
            setUnits(unitList);
          } else {
            console.log('⚠️ Response not successful, setting empty array');
            setUnits([]);
          }
        } catch (error) {
          console.error('❌ Error fetching units:', error);
          setUnits([]);
        } finally {
          setLoadingUnits(false);
        }
      } else {
        // Reset units when no company selected
        setUnits([]);
      }
    };

    fetchUnits();
  }, [formData.company_name]);

  // Fetch unit details (address and POI code) when both company and unit are selected
  useEffect(() => {
    const fetchUnitDetails = async () => {
      if (formData.company_name && formData.unit_name) {
        setLoadingUnitDetails(true);
        setIsPoiBlocked(false);
        setPoiBlockMessage('');
        try {
          console.log('📍 Fetching unit details for:', formData.company_name, '-', formData.unit_name);
          const response = await poiMappingService.getUnitDetails(
            formData.company_name,
            formData.unit_name
          );
          console.log('📦 Unit details response:', response);

          if (response && response.data) {
            const { address, poiCode: fetchedPoiCode } = response.data;
            console.log('✅ Setting unit address and POI code:', address, fetchedPoiCode);

            // Update form data with address
            setFormData(prev => ({
              ...prev,
              unit_address: address || ''
            }));

            // Store POI code in state
            setPoiCode(fetchedPoiCode || '');

            // Check if this POI is blocked due to pending cancellation payment
            if (fetchedPoiCode && vendorId) {
              try {
                const blockResult = await inspectionCallService.checkPlantPaymentBlock(fetchedPoiCode, vendorId);
                if (blockResult && blockResult.blocked) {
                  setIsPoiBlocked(true);
                  setPoiBlockMessage(
                    blockResult.message ||
                    'Call raising is blocked for this Place of Inspection due to pending cancellation charges. Please clear outstanding payment in Payment Details module.'
                  );
                }
              } catch (_) {
                // Block check failure is non-fatal; allow form to proceed
              }
            }
          } else {
            console.log('⚠️ Response not successful');
            setFormData(prev => ({ ...prev, unit_address: '' }));
            setPoiCode('');
          }
        } catch (error) {
          console.error('❌ Error fetching unit details:', error);
          setFormData(prev => ({ ...prev, unit_address: '' }));
          setPoiCode('');
        } finally {
          setLoadingUnitDetails(false);
        }
      } else {
        // Reset address and POI code when company or unit not selected
        setFormData(prev => ({ ...prev, unit_address: '' }));
        setPoiCode('');
        setIsPoiBlocked(false);
        setPoiBlockMessage('');
      }
    };

    fetchUnitDetails();
  }, [formData.company_name, formData.unit_name, vendorId]);

  // Fetch heat numbers when certificate numbers are selected
  // Aggregates heat numbers from ALL selected RM IC numbers
  useEffect(() => {
    const fetchHeatNumbers = async () => {
      if (formData.type_of_call === 'Process' && formData.process_rm_ic_numbers && formData.process_rm_ic_numbers.length > 0) {
        setLoadingHeats(true);

        try {
          console.log(`🔍 Fetching heat numbers for ${formData.process_rm_ic_numbers.length} selected RM IC(s)`);

          // Fetch heat numbers from ALL selected RM ICs
          const allHeatNumbersPromises = formData.process_rm_ic_numbers.map(async (certificateNo) => {
            try {
              console.log(`  📋 Processing certificate: ${certificateNo}`);

              // Extract call number from certificate number for API call
              // Certificate format: "ANYPART/ER-01080001/ANYPART" → Call number: "ER-01080001"
              const callNoMatch = certificateNo.match(/[^/]+\/([^/]+)\//);
              const callNo = callNoMatch ? callNoMatch[1] : certificateNo;
              console.log(`  ✂️ Extracted call number: ${callNo}`);

              const response = await inspectionCallService.getHeatNumbersByRmIcNumber(callNo);

              if (response && response.data && Array.isArray(response.data)) {
                // Add source certificate info to each heat number
                const heatNumbersWithSource = response.data.map(heat => ({
                  ...heat,
                  sourceCertificateNo: certificateNo,
                  sourceCallNo: callNo
                }));
                console.log(`  ✅ Fetched ${heatNumbersWithSource.length} heat numbers from ${certificateNo}`);
                return heatNumbersWithSource;
              } else {
                console.log(`  ⚠️ No heat numbers found for ${certificateNo}`);
                return [];
              }
            } catch (error) {
              console.error(`  ❌ Error fetching heat numbers for ${certificateNo}:`, error);
              return [];
            }
          });

          // Wait for all API calls to complete
          const allHeatNumbersArrays = await Promise.all(allHeatNumbersPromises);

          // Flatten the array of arrays into a single array
          const allHeatNumbers = allHeatNumbersArrays.flat();

          // Remove duplicates based on heat number (keep first occurrence)
          const uniqueHeatNumbers = allHeatNumbers.reduce((acc, current) => {
            const existingHeat = acc.find(heat => heat.heatNumber === current.heatNumber);
            if (!existingHeat) {
              acc.push(current);
            } else {
              // If duplicate found, log it
              console.log(`  ℹ️ Duplicate heat number found: ${current.heatNumber} (from ${current.sourceCertificateNo})`);
            }
            return acc;
          }, []);

          console.log(`📦 Total unique heat numbers from all RM ICs: ${uniqueHeatNumbers.length}`);
          setProcessHeatNumbers(uniqueHeatNumbers);
        } catch (error) {
          console.error('❌ Error fetching heat numbers:', error);
          setProcessHeatNumbers([]);
        } finally {
          setLoadingHeats(false);
        }
      } else {
        setProcessHeatNumbers([]);
      }
    };

    fetchHeatNumbers();
  }, [formData.type_of_call, formData.process_rm_ic_numbers]);

  // In modify mode: pre-seed processHeatNumbers from the pre-filled lot-heat mapping
  // so the saved heat number always appears as a dropdown option immediately.
  const [heatNumbersPreSeeded, setHeatNumbersPreSeeded] = useState(false);
  useEffect(() => {
    if (
      isModifyMode &&
      !heatNumbersPreSeeded &&
      formData.type_of_call === 'Process' &&
      formData.process_lot_heat_mapping &&
      formData.process_lot_heat_mapping.length > 0
    ) {
      setHeatNumbersPreSeeded(true);
      setProcessHeatNumbers(prev => {
        const existingKeys = new Set(prev.map(h => h.heatNumber));
        const toAdd = [];
        formData.process_lot_heat_mapping.forEach(lot => {
          if (lot.heatNumber && !existingKeys.has(lot.heatNumber)) {
            existingKeys.add(lot.heatNumber);
            toAdd.push({
              heatNumber: lot.heatNumber,
              manufacturer: lot.manufacturer || '',
              qtyAccepted: lot.totalAcceptedQtyRm || 0,
              weightAcceptedMt: 0,
              sourceCertificateNo: 'pre-filled'
            });
          }
        });
        return [...prev, ...toAdd];
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModifyMode, formData.type_of_call, formData.process_lot_heat_mapping, heatNumbersPreSeeded]);

  // Fetch company and unit details from the first selected RM IC for Process inspection
  useEffect(() => {
    const fetchCompanyAndUnitFromRmIc = async () => {
      if (formData.type_of_call === 'Process' &&
        formData.process_rm_ic_numbers &&
        formData.process_rm_ic_numbers.length > 0) {
        try {
          console.log('🏢 Fetching company and unit details from first RM IC');
          console.log('🏢 Selected RM IC certificates:', formData.process_rm_ic_numbers);

          // Get the first selected RM IC certificate number
          const firstCertificateNo = formData.process_rm_ic_numbers[0];
          console.log('🏢 First certificate:', firstCertificateNo);

          // Extract call number from certificate number
          // Certificate format: "ANYPART/ER-01080001/ANYPART" → Call number: "ER-01080001"
          const callNoMatch = firstCertificateNo.match(/[^/]+\/([^/]+)\//);
          const callNo = callNoMatch ? callNoMatch[1] : firstCertificateNo;

          console.log(`  📋 Extracted call number: ${callNo}`);
          console.log(`  📋 Fetching details for IC: ${callNo}`);

          // Fetch full RM IC details
          const response = await inspectionCallService.getRMInspectionCallByICNumber(callNo);

          console.log('  📦 Full RM IC response:', response);

          if (response && response.data) {
            const rmIcData = response.data;
            console.log('  ✅ RM IC data fetched:', rmIcData);
            console.log('  📍 Company ID from RM IC:', rmIcData.companyId, 'Type:', typeof rmIcData.companyId);
            console.log('  📍 Unit ID from RM IC:', rmIcData.unitId, 'Type:', typeof rmIcData.unitId);

            // Update form data with company and unit details from RM IC
            setFormData(prev => {
              const updated = {
                ...prev,
                company_id: rmIcData.companyId || null,
                company_name: rmIcData.companyName || '',
                unit_id: rmIcData.unitId || null,
                unit_name: rmIcData.unitName || '',
                unit_address: rmIcData.unitAddress || ''
              };
              console.log('  🔄 Updating formData with:', {
                company_id: updated.company_id,
                company_name: updated.company_name,
                unit_id: updated.unit_id,
                unit_name: updated.unit_name
              });
              return updated;
            });

            console.log('  ✅ Form update completed');
          } else {
            console.warn('  ⚠️ No data in RM IC response');
          }
        } catch (error) {
          console.error('❌ Error fetching RM IC details:', error);
          console.error('❌ Error stack:', error.stack);
        }
      } else {
        console.log('🏢 Skipping company/unit fetch - conditions not met:', {
          type_of_call: formData.type_of_call,
          has_rm_ic_numbers: formData.process_rm_ic_numbers && formData.process_rm_ic_numbers.length > 0
        });
      }
    };

    fetchCompanyAndUnitFromRmIc();
  }, [formData.type_of_call, formData.process_rm_ic_numbers]);

  // ==================== FINAL INSPECTION CALL DROPDOWN EFFECTS (NEW REVERSED FLOW) ====================

  // Step 1: Fetch RM IC certificates when Final inspection type is selected
  useEffect(() => {
    console.log('🔄 Final IC useEffect triggered - type_of_call:', formData.type_of_call, 'po_serial_no:', formData.po_serial_no, 'call_category:', formData.call_category);

    const fetchRmIcCertificates = async () => {
      if (formData.type_of_call === 'Final' && formData.po_serial_no) {
        setLoadingRmIcsForFinal(true);
        try {
          console.log('🔍 Fetching RM IC certificates for PO Serial No:', formData.po_serial_no, 'call_category:', formData.call_category);
          const response = await inspectionCallService.getRmIcCertificates(formData.po_serial_no, formData.call_category);
          console.log('📦 RM IC certificates response:', response);

          if (response && response.data) {
            const rawCertificates = Array.isArray(response.data) ? [...response.data] : [];
            const seen = new Set();
            const certificates = rawCertificates.filter(item => {
              const certNo = typeof item === 'object' ? item?.certificateNo : item;
              if (!certNo || seen.has(certNo)) return false;
              seen.add(certNo);
              return true;
            });
            console.log('✅ Setting RM IC certificates:', certificates);

            // In modify mode: ensure pre-selected RM IC numbers are available as options
            if (isModifyMode && formData.final_rm_ic_numbers && formData.final_rm_ic_numbers.length > 0) {
              const existingValues = new Set(certificates.map(d => (typeof d === 'object' ? d.certificateNo : d)));
              formData.final_rm_ic_numbers.forEach(preSelected => {
                if (!existingValues.has(preSelected)) {
                  certificates.push({
                    certificateNo: preSelected,
                    createdAt: null
                  });
                }
              });
            }

            // Hide ICs whose PO SR no has a COMPLETE diversion (unless it is a diverted call)
            const visibleCerts = (formData.call_category !== 'DIVERTED' && completeDiversionSrNos.has(formData.po_serial_no)) ? [] : certificates;
            setRmIcNumbersForFinal(visibleCerts);
          } else {
            console.warn('⚠️ No RM IC certificates found');
            if (isModifyMode && formData.final_rm_ic_numbers && formData.final_rm_ic_numbers.length > 0) {
              setRmIcNumbersForFinal(formData.final_rm_ic_numbers.map(ic => ({ certificateNo: ic, createdAt: null })));
            } else {
              setRmIcNumbersForFinal([]);
            }
          }
        } catch (error) {
          console.error('❌ Error fetching RM IC certificates:', error);
          if (isModifyMode && formData.final_rm_ic_numbers && formData.final_rm_ic_numbers.length > 0) {
            setRmIcNumbersForFinal(formData.final_rm_ic_numbers.map(ic => ({ certificateNo: ic, createdAt: null })));
          } else {
            setRmIcNumbersForFinal([]);
          }
        } finally {
          setLoadingRmIcsForFinal(false);
        }
      } else {
        setRmIcNumbersForFinal([]);
      }
    };

    fetchRmIcCertificates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.type_of_call, formData.po_serial_no, formData.call_category]);

  // Step 2: Fetch Process IC certificates when RM IC certificate is selected (or when Diverted Final is selected)
  useEffect(() => {
    const fetchProcessIcCertificates = async () => {
      if (formData.type_of_call === 'Final') {
        if (formData.call_category === 'DIVERTED') {
          // Diverted Call: Fetch Process IC of this target PO Serial No (diverted process ICs)
          if (formData.po_serial_no) {
            setLoadingProcessIcs(true);
            try {
              console.log('🔍 Fetching Diverted Process ICs for PO Serial No:', formData.po_serial_no);
              const response = await inspectionCallService.getDivertedProcessIcs(formData.po_serial_no);
              if (response && response.data) {
                const rawCertificates = Array.isArray(response.data) ? [...response.data] : [];
                const seen = new Set();
                const certificates = rawCertificates.filter(item => {
                  const certNo = typeof item === 'object' ? item?.certificateNo : item;
                  if (!certNo || seen.has(certNo)) return false;
                  seen.add(certNo);
                  return true;
                });
                setProcessIcCertificates(certificates);
              } else {
                setProcessIcCertificates([]);
              }
            } catch (error) {
              console.error('❌ Error fetching diverted Process IC certificates:', error);
              setProcessIcCertificates([]);
            } finally {
              setLoadingProcessIcs(false);
            }
          } else {
            setProcessIcCertificates([]);
          }
        } else if (formData.final_rm_ic_numbers && formData.final_rm_ic_numbers.length > 0) {
          setLoadingProcessIcs(true);
          try {
            console.log('🔍 Fetching Process IC certificates for multiple RM certificates:', formData.final_rm_ic_numbers);

            // Get Process ICs for ALL selected RM IC certificates
            const response = await inspectionCallService.getProcessIcCertificatesByMultipleRmCertificates(formData.final_rm_ic_numbers);
            console.log('📦 Process IC certificates response:', response);

            if (response && response.data) {
              const certificates = Array.isArray(response.data) ? [...response.data] : [];
              console.log('✅ Setting Process IC certificates:', certificates);

              // In modify mode: ensure pre-selected Process IC numbers are available as options
              if (isModifyMode && formData.final_process_ic_numbers && formData.final_process_ic_numbers.length > 0) {
                const existingValues = new Set(certificates.map(d => (typeof d === 'object' ? d.certificateNo : d)));
                formData.final_process_ic_numbers.forEach(preSelected => {
                  if (!existingValues.has(preSelected)) {
                    certificates.push({
                      certificateNo: preSelected,
                      createdAt: null
                    });
                  }
                });
              }

              setProcessIcCertificates(certificates);
            } else {
              console.warn('⚠️ No Process IC certificates found');
              if (isModifyMode && formData.final_process_ic_numbers && formData.final_process_ic_numbers.length > 0) {
                setProcessIcCertificates(formData.final_process_ic_numbers.map(ic => ({ certificateNo: ic, createdAt: null })));
              } else {
                setProcessIcCertificates([]);
              }
            }
          } catch (error) {
            console.error('❌ Error fetching Process IC certificates:', error);
            if (isModifyMode && formData.final_process_ic_numbers && formData.final_process_ic_numbers.length > 0) {
              setProcessIcCertificates(formData.final_process_ic_numbers.map(ic => ({ certificateNo: ic, createdAt: null })));
            } else {
              setProcessIcCertificates([]);
            }
          } finally {
            setLoadingProcessIcs(false);
          }
        } else {
          setProcessIcCertificates([]);
        }
      } else {
        setProcessIcCertificates([]);
      }
    };

    fetchProcessIcCertificates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.type_of_call, formData.final_rm_ic_numbers, formData.call_category, formData.po_serial_no]);

  // Step 3: Fetch Lot numbers when both RM IC and Process IC are selected
  useEffect(() => {
    const fetchLotNumbers = async () => {
      const hasRmIc = formData.final_rm_ic_numbers && formData.final_rm_ic_numbers.length > 0;
      const hasProcessIc = formData.final_process_ic_numbers && formData.final_process_ic_numbers.length > 0;
      const canFetch = formData.type_of_call === 'Final' && hasProcessIc && (formData.call_category === 'DIVERTED' || hasRmIc);

      if (canFetch) {
        setLoadingLotsForFinal(true);
        try {
          // Get lots based on selected RM IC and Process IC certificates (MULTI-SELECT)
          const rmCerts = hasRmIc ? formData.final_rm_ic_numbers : ['ALL'];
          console.log('🔍 Fetching lot numbers for RM certificates:', rmCerts, 'and Process certificates:', formData.final_process_ic_numbers);

          const response = await inspectionCallService.getLotNumbersByMultipleCertificates(rmCerts, formData.final_process_ic_numbers);
          console.log('📦 Lot numbers response:', response);

          if (response && response.data) {
            const lotNumbers = Array.isArray(response.data) ? [...response.data] : [];
            console.log('✅ Setting lot numbers:', lotNumbers);

            // In modify mode: ensure pre-selected lot numbers are available as options
            if (isModifyMode && formData.final_lot_numbers && formData.final_lot_numbers.length > 0) {
              const existingValues = new Set(lotNumbers);
              formData.final_lot_numbers.forEach(preSelected => {
                if (!existingValues.has(preSelected)) {
                  lotNumbers.push(preSelected);
                }
              });
            }

            setLotNumbersForFinal(lotNumbers);
          } else {
            console.warn('⚠️ No lot numbers found');
            if (isModifyMode && formData.final_lot_numbers && formData.final_lot_numbers.length > 0) {
              setLotNumbersForFinal([...formData.final_lot_numbers]);
            } else {
              setLotNumbersForFinal([]);
            }
          }
        } catch (error) {
          console.error('❌ Error fetching lot numbers:', error);
          if (isModifyMode && formData.final_lot_numbers && formData.final_lot_numbers.length > 0) {
            setLotNumbersForFinal([...formData.final_lot_numbers]);
          } else {
            setLotNumbersForFinal([]);
          }
        } finally {
          setLoadingLotsForFinal(false);
        }
      } else {
        setLotNumbersForFinal([]);
      }
    };

    fetchLotNumbers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.type_of_call, formData.final_rm_ic_numbers, formData.final_process_ic_numbers, formData.call_category]);

  // Step 4: Fetch heat numbers for each selected lot
  // Step 4: Fetch heat numbers for each selected lot
  useEffect(() => {
    const fetchHeatNumbersForLots = async () => {
      const hasRmIc = formData.final_rm_ic_numbers && formData.final_rm_ic_numbers.length > 0;
      const canFetchHeat = formData.type_of_call === 'Final' &&
        formData.final_lot_numbers && formData.final_lot_numbers.length > 0 &&
        (formData.call_category === 'DIVERTED' || hasRmIc);

      if (canFetchHeat) {
        try {
          const newLotHeatMapping = {};

          // Fetch heat numbers for each lot across all selected Process ICs and RM ICs
          for (const lotNumber of formData.final_lot_numbers) {
            let heatNumberFound = false;

            // Check across all Process ICs and RM ICs
            const procCertificates = (formData.final_process_ic_numbers && formData.final_process_ic_numbers.length > 0)
              ? formData.final_process_ic_numbers
              : [''];

            for (const procCertificate of procCertificates) {
              const rmCertificates = (hasRmIc && formData.call_category !== 'DIVERTED')
                ? formData.final_rm_ic_numbers
                : [''];

              for (const rmCertificate of rmCertificates) {
                console.log('🔍 Fetching heat numbers for lot:', lotNumber, 'with RM certificate:', rmCertificate, 'Process cert:', procCertificate);
                try {
                  const response = await inspectionCallService.getHeatNumbersByLotNumber(lotNumber, rmCertificate, procCertificate);

                  if (response && response.data) {
                    const heatNumbers = Array.isArray(response.data) ? response.data : [];
                    if (heatNumbers.length > 0 && heatNumbers[0]) {
                      newLotHeatMapping[lotNumber] = heatNumbers[0];
                      console.log(`✅ Heat number for lot ${lotNumber}:`, newLotHeatMapping[lotNumber]);
                      heatNumberFound = true;
                      break;
                    }
                  }
                } catch (e) {
                  console.warn(`Could not fetch heat number with RM ${rmCertificate} and Proc ${procCertificate}:`, e.message);
                }
              }
              if (heatNumberFound) break;
            }

            // Fallback 1: Query without certificate filter
            if (!heatNumberFound) {
              try {
                const response = await inspectionCallService.getHeatNumbersByLotNumber(lotNumber, '', '');
                if (response && response.data) {
                  const heatNumbers = Array.isArray(response.data) ? response.data : [];
                  if (heatNumbers.length > 0 && heatNumbers[0]) {
                    newLotHeatMapping[lotNumber] = heatNumbers[0];
                    console.log(`✅ Fallback heat number for lot ${lotNumber}:`, newLotHeatMapping[lotNumber]);
                    heatNumberFound = true;
                  }
                }
              } catch (fallbackErr) {
                console.warn(`Fallback heat number query failed for ${lotNumber}:`, fallbackErr.message);
              }
            }

            // Fallback 2: Check existing lot data (e.g. in modify mode)
            if (!heatNumberFound) {
              const existingLot = formData.final_lots_data?.find(l => l.lotNumber === lotNumber) ||
                selectedItem?.final_lots_data?.find(l => l.lotNumber === lotNumber);
              if (existingLot && existingLot.heatNo) {
                newLotHeatMapping[lotNumber] = existingLot.heatNo;
                heatNumberFound = true;
              }
            }

            if (!heatNumberFound) {
              newLotHeatMapping[lotNumber] = '';
            }
          }

          setLotHeatMapping(newLotHeatMapping);
        } catch (error) {
          console.error('❌ Error fetching heat numbers for lots:', error);
        }
      } else {
        setLotHeatMapping({});
      }
    };

    fetchHeatNumbersForLots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.type_of_call, formData.final_lot_numbers, formData.final_rm_ic_numbers, formData.final_process_ic_numbers, formData.call_category]);

  // Step 5: Sync final_lots_data and fetch acceptedProcessQty
  useEffect(() => {
    const syncFinalLotsData = async () => {
      if (formData.type_of_call === 'Final' &&
        formData.final_lot_numbers && formData.final_lot_numbers.length > 0 &&
        formData.final_process_ic_numbers && formData.final_process_ic_numbers.length > 0) {

        // Extract Request IDs from all selected Process IC numbers
        const requestIds = (formData.final_process_ic_numbers || []).map(processCert => {
          const callNoMatch = processCert.match(/[^/]+\/([^/]+)\//);
          return callNoMatch ? callNoMatch[1] : processCert;
        }).filter(Boolean);

        const updatedLotsData = await Promise.all(formData.final_lot_numbers.map(async (lotNumber) => {
          const existing = formData.final_lots_data?.find(l => l.lotNumber === lotNumber) ||
            selectedItem?.final_lots_data?.find(l => l.lotNumber === lotNumber);
          const heatNo = lotHeatMapping[lotNumber] || existing?.heatNo || '';

          let acceptedQtyProcess = existing?.acceptedQtyProcess || 0;
          let offeredEarlier = existing?.offeredEarlier || 0;

          // Fetch values if heatNo is available
          if (heatNo) {
            try {
              // 1. Fetch acceptedQtyProcess across all request IDs (summing quantities from all selected Process ICs)
              let totalFetchedAcceptedQty = 0;
              for (const requestId of requestIds) {
                try {
                  const acceptedRes = await inspectionCallService.getAcceptedQtyForLot(requestId, lotNumber, heatNo);
                  if (acceptedRes && acceptedRes.success && acceptedRes.data > 0) {
                    totalFetchedAcceptedQty += (parseInt(acceptedRes.data, 10) || 0);
                  }
                } catch (err) {
                  console.warn(`Error fetching accepted qty with reqId ${requestId}:`, err.message);
                }
              }

              // Fallback: If no qty found with individual requestIds, try without requestId
              if (totalFetchedAcceptedQty === 0) {
                try {
                  const fallbackRes = await inspectionCallService.getAcceptedQtyForLot('', lotNumber, heatNo);
                  if (fallbackRes && fallbackRes.success && fallbackRes.data > 0) {
                    totalFetchedAcceptedQty = parseInt(fallbackRes.data, 10) || 0;
                  }
                } catch (fallbackErr) {
                  console.warn(`Fallback accepted qty query failed for ${lotNumber}:`, fallbackErr.message);
                }
              }

              if (totalFetchedAcceptedQty > 0) {
                acceptedQtyProcess = totalFetchedAcceptedQty;
              }

              // 2. Fetch offeredEarlier for this specific PO and PO Serial Number
              const currentPoNo = formData.po_no || selectedPO?.po_no || selectedItem?.po_no || '';
              const currentPoSerialNo = formData.po_serial_no || selectedPoSerial || selectedItem?.po_serial_no || '';
              const earlierRes = await inspectionCallService.getOfferedEarlierQuantity(heatNo, lotNumber, currentPoNo, currentPoSerialNo);
              if (earlierRes && earlierRes.success) {
                offeredEarlier = earlierRes.data || 0;

                // In modify mode, the backend's offeredEarlier already includes the original call's quantity.
                // We need to subtract the original quantity for this lot from this call so it doesn't get double-counted.
                if (isModifyMode && selectedItem && selectedItem.final_lots_data) {
                  const originalLot = selectedItem.final_lots_data.find(l => l.lotNumber === lotNumber);
                  if (originalLot && originalLot.offeredQty) {
                    const originalOfferedQty = parseInt(originalLot.offeredQty) || 0;
                    if (originalOfferedQty > 0) {
                      offeredEarlier = Math.max(0, offeredEarlier - originalOfferedQty);
                    }
                  }
                }
              }
            } catch (error) {
              console.error(`Error fetching data for lot ${lotNumber}:`, error);
            }
          }

          const totalAvailable = Math.max(0, acceptedQtyProcess - offeredEarlier);
          const currentOffered = existing?.offeredQty !== undefined && existing?.offeredQty !== '' ? (parseInt(existing.offeredQty) || 0) : 0;
          const futureBalance = Math.max(0, totalAvailable - currentOffered);

          return {
            lotNumber,
            heatNo,
            acceptedQtyProcess,
            offeredEarlier,
            futureBalance,
            offeredQty: existing?.offeredQty !== undefined ? existing.offeredQty : '',
            noOfBags: existing?.noOfBags !== undefined ? existing.noOfBags : ''
          };
        }));

        setFormData(prev => ({ ...prev, final_lots_data: updatedLotsData }));
      } else {
        setFormData(prev => ({ ...prev, final_lots_data: [] }));
      }
    };

    syncFinalLotsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.type_of_call, formData.final_lot_numbers, formData.final_process_ic_numbers, lotHeatMapping]);

  // Get available RM ICs for Process stage
  // const availableRmIcs = useMemo(() => {
  //   return RM_INSPECTION_CALLS.filter(ic => ic.status === 'Completed');
  // }, []);

  // Get available Process ICs for Final stage
  // const availableProcessIcs = useMemo(() => {
  //   return PROCESS_INSPECTION_CALLS.filter(ic => ic.status === 'Completed');
  // }, []);

  // Get available lot numbers
  // const availableLots = useMemo(() => {
  //   return LOT_NUMBERS.filter(l => l.qtyAvailable > 0);
  // }, []);

  // Get units for selected company - COMMENTED OUT - now using POI API
  // const unitOptions = useMemo(() => {
  //   const company = COMPANY_UNIT_MASTER.find(c => c.id === parseInt(formData.company_id));
  //   return company?.units || [];
  // }, [formData.company_id]);

  // ERC Division Factor helper based on specification:
  // MK-V: 1.14, MK-III: 0.91, J-Type: 0.915
  const getErcDivisionFactor = useCallback((ercType) => {
    const typeUpper = (ercType || '').toUpperCase().trim();
    if (typeUpper.includes('MK-III') || typeUpper.includes('MKIII') || typeUpper.includes('MK 3') || typeUpper.includes('MK-3')) {
      return 0.91;
    }
    if (typeUpper.includes('J') || typeUpper.includes('J-TYPE') || typeUpper.includes('J TYPE')) {
      return 0.915;
    }
    // Default to MK-V: 1.14
    return 1.14;
  }, []);

  // Calculate ERC quantity from MT (for Raw Material)
  const calculateErcFromMt = useCallback((mtQty, ercType) => {
    if (!mtQty || !ercType) return 0;

    const qty = parseFloat(mtQty);
    if (isNaN(qty) || qty <= 0) return 0;

    const divisor = getErcDivisionFactor(ercType);
    const maxErc = (qty * 1000) / divisor;

    return Math.floor(maxErc);
  }, [getErcDivisionFactor]);

  // Calculate maximum ERC that can be manufactured from a specific heat-TC mapping
  const calculateMaxErcForHeat = useCallback((offeredQty, ercType) => {
    if (!offeredQty || !ercType) return 0;

    const qty = parseFloat(offeredQty);
    if (isNaN(qty) || qty <= 0) return 0;

    const divisor = getErcDivisionFactor(ercType);
    const maxErc = (qty * 1000) / divisor;

    return Math.floor(maxErc);
  }, [getErcDivisionFactor]);

  // Format number with thousand separators
  const formatNumber = useCallback((num) => {
    if (!num || isNaN(num)) return '0';
    return num.toLocaleString('en-IN');
  }, []);

  // Calculate remaining quantity for each stage
  // eslint-disable-next-line no-unused-vars
  const remainingQty = useMemo(() => {
    const poSerial = PO_SERIAL_DETAILS.find(p => p.serialNo === formData.po_serial_no);
    if (!poSerial) return { rm: 0, process: 0, final: 0 };

    return {
      rm: poSerial.poQty - poSerial.qtyAlreadyInspected.rm,
      process: poSerial.qtyAlreadyInspected.rm - poSerial.qtyAlreadyInspected.process,
      final: poSerial.qtyAlreadyInspected.process - poSerial.qtyAlreadyInspected.final
    };
  }, [formData.po_serial_no]);

  // Auto-calculate total offered quantity and ERC when heat-TC mappings change
  useEffect(() => {
    if (formData.type_of_call === 'Raw Material' && formData.rm_heat_tc_mapping.length > 0) {
      const totalMt = formData.rm_heat_tc_mapping.reduce((sum, heat) => {
        return sum + (parseFloat(heat.offeredQty) || 0);
      }, 0);
      // Pass the type_of_erc to calculate correctly based on selected ERC type
      const ercQty = calculateErcFromMt(totalMt, formData.type_of_erc);
      setFormData(prev => ({
        ...prev,
        rm_total_offered_qty_mt: totalMt,
        rm_offered_qty_erc: ercQty
      }));
    } else if (formData.type_of_call === 'Raw Material' && formData.rm_heat_tc_mapping.length === 0) {
      setFormData(prev => ({
        ...prev,
        rm_total_offered_qty_mt: 0,
        rm_offered_qty_erc: 0
      }));
    }
  }, [formData.rm_heat_tc_mapping, formData.type_of_call, formData.type_of_erc, calculateErcFromMt]);

  // Note: Validation for total quantity against PO Serial Quantity has been removed as per user request.



  // Use availableHeatNumbers from props (fetched from /api/vendor/available-heat-numbers/{vendorCode})
  // This endpoint returns only heat numbers with:
  // - Remaining quantity > 0
  // - Status != EXHAUSTED
  //
  // Note: UNDER_INSPECTION, ACCEPTED, and REJECTED entries are still available for selection.
  // Only EXHAUSTED entries are filtered out at the backend level.

  // If availableHeatNumbers prop is empty, fall back to filtering inventoryEntries (for backward compatibility)
  const heatNumbersForDropdown = useMemo(() => {
    // Helper function to filter by grade based on ERC type and Call type
    const filterByGrade = (entries) => {
      if (formData.type_of_call !== 'Raw Material') {
        return entries;
      }
      if (!formData.type_of_erc || formData.type_of_erc === '') {
        return entries;
      }

      // Filter based on Type of ERC
      if (formData.type_of_erc === 'MK-III') {
        const filtered = entries.filter(entry => {
          const grade = (entry.gradeSpecification || '').toLowerCase().replace(/\s+/g, '');
          if (!grade) return true;
          return grade.includes('55si7') || grade.includes('20.64');
        });
        return filtered.length > 0 ? filtered : entries;
      } else if (formData.type_of_erc === 'MK-V') {
        const filtered = entries.filter(entry => {
          const grade = (entry.gradeSpecification || '').toLowerCase().replace(/\s+/g, '');
          if (!grade) return true;
          return grade.includes('55si7') || grade.includes('23');
        });
        return filtered.length > 0 ? filtered : entries;
      }

      return entries;
    };

    if (availableHeatNumbers && availableHeatNumbers.length > 0) {
      console.log('✅ Using availableHeatNumbers from API:', availableHeatNumbers.length);
      const gradeFiltered = filterByGrade(availableHeatNumbers);
      return gradeFiltered;
    }

    // Fallback: filter from inventoryEntries
    console.log('⚠️ Falling back to filtering inventoryEntries');

    const filtered = (inventoryEntries || [])
      .filter(entry => {
        // Explicitly exclude EXHAUSTED status only when NOT in modify mode
        if (!isModifyMode && (entry.status === 'EXHAUSTED' || entry.status === 'Exhausted')) {
          return false;
        }

        // In modify mode, include entries so vendor can pick from existing inventory
        const hasQuantity = isModifyMode || (entry.qtyLeftForInspection > 0);
        return hasQuantity;
      })
      .map(entry => ({
        heatNumber: entry.heatNumber,
        tcNumber: entry.tcNumber,
        rawMaterial: entry.rawMaterial,
        supplierName: entry.supplierName,
        gradeSpecification: entry.gradeSpecification,
        qtyLeft: entry.qtyLeftForInspection,
        unit: entry.unitOfMeasurement,
        status: entry.status
      }));

    console.log(`📊 Fallback filtered ${filtered.length} available heat numbers from ${inventoryEntries.length} total entries`);

    // Apply grade filtering to fallback results
    const gradeFiltered = filterByGrade(filtered);
    return gradeFiltered;
  }, [availableHeatNumbers, inventoryEntries, formData.type_of_erc, formData.type_of_call, isModifyMode]);

  // Get available heat numbers for a specific heat mapping dropdown
  // Deduplicates based on composite key (heatNumber + supplierName)
  const getAvailableHeatNumbersForDropdown = useCallback((currentHeatMappingId) => {
    const seenCompositeKeys = new Set();
    const deduplicatedHeats = [];

    // 1. Always include all heats present in formData.rm_heat_tc_mapping (for all sections)
    (formData.rm_heat_tc_mapping || []).forEach(mapping => {
      if (mapping && mapping.heatNumber) {
        const heatObj = {
          heatNumber: mapping.heatNumber,
          supplierName: mapping.supplierName || mapping.manufacturer || '',
          rawMaterial: mapping.unit || '',
          gradeSpecification: '',
          qtyLeft: parseFloat(mapping.tcQtyRemaining) || parseFloat(mapping.maxQty) || 0,
          unit: mapping.unit || ''
        };
        const compositeKey = `${heatObj.heatNumber}|${heatObj.supplierName}`;
        if (!seenCompositeKeys.has(compositeKey)) {
          seenCompositeKeys.add(compositeKey);
          deduplicatedHeats.push(heatObj);
        }
      }
    });

    // 2. Also include all heats from heatNumbersForDropdown (from inventory entries)
    (heatNumbersForDropdown || []).forEach(heat => {
      const compositeKey = `${heat.heatNumber}|${heat.supplierName}`;
      if (!seenCompositeKeys.has(compositeKey)) {
        seenCompositeKeys.add(compositeKey);
        deduplicatedHeats.push(heat);
      }
    });

    console.log(`📋 Heat numbers for dropdown ${currentHeatMappingId}:`, {
      total: heatNumbersForDropdown.length,
      deduplicated: deduplicatedHeats.length
    });

    return deduplicatedHeats;
  }, [heatNumbersForDropdown, formData.rm_heat_tc_mapping]);

  // Get available TC numbers for a specific heat number and supplier/manufacturer
  // Filters out TC numbers that are already selected in other heat sections
  const getAvailableTcNumbers = useCallback((heatNumber, supplierName = null, currentHeatMappingId = null) => {
    if (!heatNumber) return [];

    const tcList = [];

    // 1. Find all inventory entries matching this heat number and supplier
    const matchingEntries = (inventoryEntries || []).filter(entry => {
      const heatMatches = entry.heatNumber === heatNumber;
      const supplierMatches = !supplierName || entry.supplierName === supplierName;
      const hasRemainingQty = isModifyMode || (entry.qtyLeftForInspection > 0);
      return heatMatches && supplierMatches && hasRemainingQty;
    });

    matchingEntries.forEach(entry => {
      if (!tcList.find(tc => tc.tcNumber === entry.tcNumber)) {
        tcList.push({
          tcNumber: entry.tcNumber,
          heatNumber: entry.heatNumber,
          manufacturer: entry.supplierName,
          tcDate: entry.tcDate
        });
      }
    });

    // 2. Also ensure all TCs present in formData.rm_heat_tc_mapping for this heat are included
    (formData.rm_heat_tc_mapping || []).forEach(h => {
      if (h.heatNumber === heatNumber && h.tcNumber && !tcList.find(tc => tc.tcNumber === h.tcNumber)) {
        tcList.push({
          tcNumber: h.tcNumber,
          heatNumber: h.heatNumber,
          manufacturer: h.manufacturer || h.supplierName || '',
          tcDate: h.tcDate
        });
      }
    });

    // Fallback to old HEAT_TC_MAPPING if not found in inventory
    if (tcList.length === 0) {
      const heat = HEAT_TC_MAPPING.find(h => h.heatNumber === heatNumber && (!supplierName || h.manufacturer === supplierName));
      if (heat) {
        tcList.push({
          tcNumber: heat.tcNumber,
          heatNumber: heat.heatNumber,
          manufacturer: heat.manufacturer,
          tcDate: heat.tcDate
        });
      }
    }

    // 3. Filter out TC numbers that are already selected in OTHER rows for the SAME heat number
    const selectedTcNumbersForThisHeat = formData.rm_heat_tc_mapping
      .filter(heat => heat.id !== currentHeatMappingId && heat.tcNumber && heat.heatNumber === heatNumber)
      .map(heat => heat.tcNumber);

    const filteredTcList = tcList.filter(tc => !selectedTcNumbersForThisHeat.includes(tc.tcNumber));

    return filteredTcList;
  }, [inventoryEntries, formData.rm_heat_tc_mapping, isModifyMode]);

  // OLD CODE - Commented out as we now handle per heat-TC combination
  // const availableTcNumbers = useMemo(() => { ... }, [formData.rm_heat_numbers]);
  // useEffect(() => { ... }, [formData.rm_tc_number]);

  // Handle unit selection - COMMENTED OUT - now using POI API
  // const handleUnitChange = (unitId) => {
  //   const company = COMPANY_UNIT_MASTER.find(c => c.id === parseInt(formData.company_id));
  //   const unit = company?.units.find(u => u.id === parseInt(unitId));
  //   setFormData(prev => ({
  //     ...prev,
  //     unit_id: unitId,
  //     unit_name: unit?.unitName || '',
  //     unit_address: unit?.address || '',
  //     unit_gstin: unit?.gstin || '',
  //     unit_contact_person: unit?.contactPerson || '',
  //     unit_role: unit?.roleOfUnit || ''
  //   }));
  // };

  // Handle RM IC selection for Process stage
  const handleRmIcSelection = async (selectedIcNumbers) => {
    // Get book/set numbers for selected ICs
    const bookSetNos = selectedIcNumbers.map(icNumber => {
      const ic = RM_INSPECTION_CALLS.find(ic => ic.icNumber === icNumber);
      return { icNumber, bookSetNo: ic?.bookSetNo || '' };
    });

    // Calculate total accepted quantity from selected RM ICs
    const totalAccepted = selectedIcNumbers.reduce((sum, icNumber) => {
      const ic = RM_INSPECTION_CALLS.find(ic => ic.icNumber === icNumber);
      return sum + (ic?.qtyAccepted || 0);
    }, 0);

    // Fetch company_id and unit_id from the first selected RM IC
    let companyId = '';
    let unitId = '';
    let companyName = '';
    let unitName = '';
    let unitAddress = '';

    if (selectedIcNumbers.length > 0) {
      try {
        const certificateNo = selectedIcNumbers[0];
        console.log(`📋 Fetching company and unit details from first RM IC Certificate: ${certificateNo}`);

        // Use the certificate number to fetch RM IC details
        const rmIcDetails = await inspectionCallService.getRmIcDetailsByCertificateNo(certificateNo);

        console.log(`📊 Full API Response:`, rmIcDetails);

        if (rmIcDetails && rmIcDetails.data) {
          const details = rmIcDetails.data;
          console.log(`📋 Extracted details object:`, details);

          // Try both camelCase and snake_case field names
          companyId = details.companyId || details.company_id || '';
          unitId = details.unitId || details.unit_id || '';
          companyName = details.companyName || details.company_name || '';
          unitName = details.unitName || details.unit_name || '';
          unitAddress = details.unitAddress || details.unit_address || '';

          console.log(`✅ Fetched company and unit details:`, {
            companyId,
            unitId,
            companyName,
            unitName,
            unitAddress
          });

          // If company_id or unit_id is NULL, we need to look them up from company/unit master
          if (!companyId || !unitId) {
            console.warn(`⚠️ WARNING: companyId or unitId is NULL in RM IC record!`);
            console.warn(`   This RM IC uses POI code instead of company/unit IDs`);
            console.warn(`   Company Name: ${companyName}, Unit Name: ${unitName}`);
            console.log(`📍 Will fetch company/unit IDs from master table using company name and unit name`);
          }
        } else {
          console.error(`❌ No data in API response:`, rmIcDetails);
        }
      } catch (error) {
        console.error(`❌ Error fetching RM IC details:`, error);
        console.error(`   Error message: ${error.message}`);
        console.error(`   Error status: ${error.status}`);
        console.error(`   Full error object:`, error);
        if (error.response) {
          console.error(`   Response status: ${error.response.status}`);
          console.error(`   Response data:`, error.response.data);
        }
      }
    }

    setFormData(prev => ({
      ...prev,
      process_rm_ic_numbers: selectedIcNumbers,
      process_book_set_nos: bookSetNos,
      process_total_accepted_qty_rm: totalAccepted,
      company_id: companyId,
      unit_id: unitId,
      company_name: companyName,
      unit_name: unitName,
      unit_address: unitAddress
    }));

    // Fetch heat summary data for selected RM ICs
    if (selectedIcNumbers.length > 0) {
      await fetchHeatSummaryData(selectedIcNumbers);
    } else {
      setHeatSummaryData([]);
    }
  };

  // Fetch heat summary data for selected RM IC numbers
  const fetchHeatSummaryData = async (selectedRmIcNumbers) => {
    setLoadingHeatSummary(true);
    try {
      console.log('📊 Fetching heat summary for RM ICs:', selectedRmIcNumbers);

      const summaryData = [];

      // Process all RM ICs concurrently
      await Promise.all(selectedRmIcNumbers.map(async (rmIcNumber) => {
        try {
          // Step 1: Get PO number from RM IC
          console.log(`  📋 Getting PO number for RM IC: ${rmIcNumber}`);
          const poResponse = await inspectionCallService.getPoNumberByRmIc(rmIcNumber);

          if (poResponse && poResponse.data) {
            const poNo = poResponse.data;
            console.log(`  ✅ Got PO number: ${poNo}`);

            // Step 2: Get heat numbers from the selected RM IC
            // Extract call number from RM IC number for API call
            const callNoMatch = rmIcNumber.match(/[^/]+\/([^/]+)\//);
            const callNo = callNoMatch ? callNoMatch[1] : rmIcNumber;

            const heatResponse = await inspectionCallService.getHeatNumbersByRmIcNumber(callNo);

            if (heatResponse && heatResponse.data && Array.isArray(heatResponse.data)) {
              const heatNumbers = heatResponse.data;
              console.log(`  ✅ Got ${heatNumbers.length} heat numbers for RM IC: ${rmIcNumber}`);

              const currentVendorCode = localStorage.getItem('vendorCode') || localStorage.getItem('userName') || '';

              // Step 3: For each heat number, fetch the summary data concurrently
              const heatSummaryPromises = heatNumbers.map(async (heat) => {
                try {
                  const heatNo = heat.heatNumber;
                  console.log(`    🔥 Fetching summary for Heat: ${heatNo}, PO: ${poNo}, RM Call: ${callNo}, Vendor: ${currentVendorCode}`);

                  const summaryResponse = await inspectionCallService.getHeatSummaryData(heatNo, poNo, callNo, currentVendorCode);

                  if (summaryResponse && summaryResponse.data) {
                    const heatData = summaryResponse.data;

                    // Get offeredEarlier from API response (calculated by backend strictly for this vendor/PO)
                    const offeredEarlier = heatData.offeredEarlier || 0;
                    console.log(`    📊 Offered Earlier for ${heatNo}: ${offeredEarlier}`);

                    // Use the accepted MT strictly from this specific RM IC record
                    const acceptedMt = (heat.weightAcceptedMt !== undefined && heat.weightAcceptedMt !== null && heat.weightAcceptedMt > 0)
                      ? heat.weightAcceptedMt
                      : (heatData.weightAcceptedMt || 0);

                    // Compute Max ERC strictly from this RM IC's accepted MT
                    const ercDivisor = getErcDivisionFactor(formData.product_model || formData.type_of_erc);
                    const maxErc = (heatData.rmAcceptedQty && heatData.rmAcceptedQty > 0)
                      ? heatData.rmAcceptedQty
                      : (acceptedMt > 0 ? Math.round((acceptedMt * 1000) / ercDivisor) : 0);

                    // Calculate Future Balance = Max ERC - Offered Earlier
                    const futureBalance = maxErc - offeredEarlier;

                    // Determine status based on future balance
                    let status = 'Good';
                    if (futureBalance < 0) {
                      status = 'Critical';
                    } else if (futureBalance === 0) {
                      status = 'Exhausted';
                    }

                    return {
                      heatNo: heatData.heatNo || heatNo,
                      acceptedMt: acceptedMt,
                      maxErc: maxErc,
                      manufactured: heatData.manufaturedQty || 0,
                      offeredEarlier: offeredEarlier,
                      offeredNow: 0, // Will be updated when user enters lot details
                      futureBalance: futureBalance,
                      status: status,
                      rmIcNumber: rmIcNumber,
                      poNo: poNo
                    };
                  }
                } catch (error) {
                  console.error(`    ❌ Error fetching summary for heat ${heat.heatNumber}:`, error);
                }
                return null;
              });

              // Wait for all heat summaries for this RM IC to resolve
              const heatSummaries = await Promise.all(heatSummaryPromises);

              // Add valid summaries to the array
              heatSummaries.forEach(summary => {
                if (summary) {
                  summaryData.push(summary);
                }
              });
            }
          }
        } catch (error) {
          console.error(`  ❌ Error processing RM IC ${rmIcNumber}:`, error);
        }
      }));

      // Remove duplicates based on heat number
      const uniqueSummaryData = summaryData.reduce((acc, current) => {
        const exists = acc.find(item => item.heatNo === current.heatNo);
        if (!exists) {
          acc.push(current);
        }
        return acc;
      }, []);

      console.log('📊 Final heat summary data before adjustment:', uniqueSummaryData);

      // Adjust offeredEarlier and offeredNow based on current form data
      const adjustedSummaryData = uniqueSummaryData.map(heat => {
        // Find total offered qty for this heat in the current form (from all lots)
        const currentCallOfferedQty = formData.process_lot_heat_mapping
          .filter(lot => lot.heatNumber === heat.heatNo)
          .reduce((sum, lot) => sum + (parseInt(lot.offeredQty) || 0), 0);

        let adjustedOfferedEarlier = heat.offeredEarlier;

        // In modify mode, the backend's offeredEarlier already includes this call's quantity.
        // We need to subtract it so it doesn't get double-counted.
        if (isModifyMode && currentCallOfferedQty > 0) {
          adjustedOfferedEarlier = Math.max(0, adjustedOfferedEarlier - currentCallOfferedQty);
        }

        // Calculate new future balance
        const newFutureBalance = heat.maxErc - adjustedOfferedEarlier - currentCallOfferedQty;

        // Determine status
        let newStatus = 'Good';
        if (newFutureBalance < 0) {
          newStatus = 'Critical';
        } else if (newFutureBalance === 0) {
          newStatus = 'Exhausted';
        }

        return {
          ...heat,
          offeredEarlier: adjustedOfferedEarlier,
          offeredNow: currentCallOfferedQty,
          futureBalance: newFutureBalance,
          status: newStatus
        };
      });

      console.log('📊 Final heat summary data after adjustment:', adjustedSummaryData);
      setHeatSummaryData(adjustedSummaryData);
    } catch (error) {
      console.error('❌ Error fetching heat summary data:', error);
      setHeatSummaryData([]);
    } finally {
      setLoadingHeatSummary(false);
    }
  };

  // ========== PROCESS STAGE: LOT-HEAT MAPPING HANDLERS ==========

  // Add new lot-heat entry
  const handleAddProcessLotHeat = () => {
    setFormData(prev => ({
      ...prev,
      process_lot_heat_mapping: [
        ...prev.process_lot_heat_mapping,
        {
          id: Date.now(),
          lotNumber: '',
          manufacturerHeat: '',
          heatNumber: '',
          manufacturer: '',
          compositeKey: '',
          offeredQty: '',
          totalAcceptedQtyRm: 0,
          tentativeStartDate: '',
          isLoading: false
        }
      ]
    }));
  };

  // Remove lot-heat entry
  const handleRemoveProcessLotHeat = (id) => {
    setFormData(prev => ({
      ...prev,
      process_lot_heat_mapping: prev.process_lot_heat_mapping.filter(item => item.id !== id)
    }));
  };

  // Handle lot number change
  const handleProcessLotNumberChange = (id, lotNumber) => {
    setFormData(prev => ({
      ...prev,
      process_lot_heat_mapping: prev.process_lot_heat_mapping.map(item =>
        item.id === id ? { ...item, lotNumber } : item
      )
    }));
  };

  // Handle manufacturer-heat selection and auto-fetch details
  const handleProcessManufacturerHeatChange = (id, manufacturerHeat, heatNumber = null, manufacturer = null, maxQty = null, totalAcceptedQtyRm = null, compositeKey = null) => {
    // If heatNumber and manufacturer are not provided, parse from manufacturerHeat string
    if (!heatNumber || !manufacturer) {
      const parts = manufacturerHeat.split(' - ');
      manufacturer = parts[0] || '';
      heatNumber = parts[1] || '';
    }

    setFormData(prev => ({
      ...prev,
      process_lot_heat_mapping: prev.process_lot_heat_mapping.map(item =>
        item.id === id ? {
          ...item,
          manufacturerHeat,
          manufacturer,
          heatNumber,
          compositeKey: compositeKey || `${heatNumber}|${manufacturer}`,
          maxQty: maxQty || item.maxQty,
          totalAcceptedQtyRm: totalAcceptedQtyRm || item.totalAcceptedQtyRm
        } : item
      )
    }));
  };

  // Handle offered quantity change for process lot-heat
  const handleProcessOfferedQtyChange = (id, offeredQty) => {
    // Update form data with new offered quantity
    setFormData(prev => ({
      ...prev,
      process_lot_heat_mapping: prev.process_lot_heat_mapping.map(item =>
        item.id === id ? { ...item, offeredQty: parseInt(offeredQty) || 0 } : item
      )
    }));

    // Update heat summary data with new offered quantity
    // Find the lot-heat entry to get the heat number
    const lotHeatEntry = formData.process_lot_heat_mapping.find(item => item.id === id);
    if (lotHeatEntry && lotHeatEntry.heatNumber) {
      const newOfferedQty = parseInt(offeredQty) || 0;

      // Update the heat summary data
      setHeatSummaryData(prev =>
        prev.map(heat => {
          if (heat.heatNo === lotHeatEntry.heatNumber) {
            // Calculate total offered quantity for this heat across ALL lots (excluding current lot)
            const totalOfferedForHeat = formData.process_lot_heat_mapping
              .filter(lot => lot.heatNumber === lotHeatEntry.heatNumber && lot.id !== id)
              .reduce((sum, lot) => sum + (parseInt(lot.offeredQty) || 0), 0);

            // Add the new offered quantity for the current lot
            const totalOfferedIncludingCurrent = totalOfferedForHeat + newOfferedQty;

            // Calculate new future balance: Max ERC - Offered Earlier - Total Offered (all lots)
            const newFutureBalance = heat.maxErc - (heat.offeredEarlier || 0) - totalOfferedIncludingCurrent;

            // Determine new status based on future balance
            let newStatus = 'Good';
            if (newFutureBalance < 0) {
              newStatus = 'Critical';
            } else if (newFutureBalance === 0) {
              newStatus = 'Exhausted';
            }

            console.log(`📊 Updating heat summary for ${heat.heatNo}:`, {
              offeredNow: totalOfferedIncludingCurrent,
              futureBalance: newFutureBalance,
              status: newStatus,
              breakdown: {
                maxErc: heat.maxErc,
                manufactured: heat.manufactured,
                totalOfferedOtherLots: totalOfferedForHeat,
                currentLotOffered: newOfferedQty,
                totalOffered: totalOfferedIncludingCurrent
              }
            });

            return {
              ...heat,
              offeredNow: totalOfferedIncludingCurrent,
              futureBalance: newFutureBalance,
              status: newStatus
            };
          }
          return heat;
        })
      );
    }
  };

  // Handle tentative start date change
  const handleProcessTentativeStartDateChange = (id, tentativeStartDate) => {
    setFormData(prev => ({
      ...prev,
      process_lot_heat_mapping: prev.process_lot_heat_mapping.map(item =>
        item.id === id ? { ...item, tentativeStartDate } : item
      )
    }));
  };

  // ========== FINAL STAGE HANDLERS ==========

  // Note: handleFinalLotSelection and handleFinalProcessIcSelection were removed
  // as they were unused. The form directly updates formData in onChange handlers.

  // Auto-calculate Total Qty and Total Bags for Final stage
  useEffect(() => {
    if (formData.type_of_call === 'Final' && formData.final_lots_data.length > 0) {
      const totalQty = formData.final_lots_data.reduce((sum, lot) => sum + (parseInt(lot.offeredQty) || 0), 0);
      const totalBags = formData.final_lots_data.reduce((sum, lot) => sum + (parseInt(lot.noOfBags) || 0), 0);

      setFormData(prev => ({
        ...prev,
        final_erc_qty: totalQty,
        final_total_qty: totalQty.toString(),
        final_hdpe_bags: totalBags
      }));
    } else if (formData.type_of_call === 'Final') {
      setFormData(prev => ({
        ...prev,
        final_erc_qty: '',
        final_total_qty: '',
        final_hdpe_bags: ''
      }));
    }
  }, [formData.type_of_call, formData.final_lots_data]);

  // Handle final lot data change
  const handleFinalLotDataChange = (lotNumber, field, value) => {
    setFormData(prev => ({
      ...prev,
      final_lots_data: (prev.final_lots_data || []).map(l => {
        if (l.lotNumber === lotNumber) {
          let newValue = value;

          // Max available for this lot before this call's offer
          const totalAvailable = Math.max(0, (l.acceptedQtyProcess || 0) - (l.offeredEarlier || 0));

          if (field === 'offeredQty') {
            const numVal = parseInt(value) || 0;
            if (numVal > totalAvailable) {
              newValue = totalAvailable;
            }
          }

          const currentOffered = field === 'offeredQty' ? (parseInt(newValue) || 0) : (parseInt(l.offeredQty) || 0);
          const dynamicFutureBalance = Math.max(0, totalAvailable - currentOffered);

          const updatedLot = {
            ...l,
            [field]: newValue,
            futureBalance: dynamicFutureBalance
          };

          // Logic: No. of Bags >= (Qty of that Lot / 50)
          // Auto-calculate suggested bags when Qty is changed
          if (field === 'offeredQty') {
            const numVal = parseInt(newValue) || 0;
            const minBags = Math.ceil(numVal / 50);
            // If current bags are 0 or less than the new minimum, auto-update them
            if (!l.noOfBags || l.noOfBags < minBags) {
              updatedLot.noOfBags = minBags;
            }
          }
          return updatedLot;
        }
        return l;
      })
    }));
  };

  // === NEW: Heat-TC Mapping Handlers ===

  // Add new heat number section
  const handleAddHeatNumber = () => {
    setFormData(prev => ({
      ...prev,
      rm_heat_tc_mapping: [
        ...prev.rm_heat_tc_mapping,
        {
          id: Date.now(),
          heatNumber: '',
          supplierName: '',
          compositeKey: '',
          tcNumber: '',
          tcDate: '',
          manufacturer: '',
          invoiceNo: '',
          invoiceDate: '',
          subPoNumber: '',
          subPoDate: '',
          subPoQty: '',
          subPoTotalValue: '',
          tcQty: '',
          tcQtyRemaining: '',
          offeredQty: '',
          maxQty: '',
          unit: '',
          isLoading: false,
          isLoadingChemical: false,
          chemicalAutoFetched: false,
          chemicalReadOnly: false,
          // Chemical Analysis - Now per heat number
          chemical_carbon: '',
          chemical_manganese: '',
          chemical_silicon: '',
          chemical_sulphur: '',
          chemical_phosphorus: ''
        }
      ]
    }));
  };

  // Remove heat number section
  const handleRemoveHeatNumber = (id) => {
    setFormData(prev => ({
      ...prev,
      rm_heat_tc_mapping: prev.rm_heat_tc_mapping.filter(heat => heat.id !== id)
    }));
  };

  // Handle heat number selection for a specific section
  const handleHeatNumberChange = async (id, heatNumber, supplierName = '', compositeKey = '') => {
    // First, update the heat number and reset other fields
    setFormData(prev => ({
      ...prev,
      rm_heat_tc_mapping: prev.rm_heat_tc_mapping.map(heat =>
        heat.id === id
          ? {
            ...heat,
            heatNumber,
            supplierName,
            compositeKey,
            tcNumber: '',
            tcDate: '',
            manufacturer: '',
            invoiceNo: '',
            invoiceDate: '',
            subPoNumber: '',
            subPoDate: '',
            subPoQty: '',
            subPoTotalValue: '',
            tcQty: '',
            tcQtyRemaining: '',
            maxQty: '',
            unit: '',
            isLoadingChemical: heatNumber ? true : false,
            chemicalReadOnly: false // Default to editable
          }
          : heat
      )
    }));

    // Auto-fetch chemical analysis if heat number is provided
    if (heatNumber) {
      // 1. Check if this heat number already exists in ANOTHER row of the current form
      const existingEntry = formData.rm_heat_tc_mapping.find(
        h => h.id !== id && h.heatNumber === heatNumber && h.chemical_carbon !== ''
      );

      if (existingEntry) {
        console.log(`🔬 Found existing entry for heat ${heatNumber} in the current form. Auto-populating...`);
        setFormData(prev => ({
          ...prev,
          rm_heat_tc_mapping: prev.rm_heat_tc_mapping.map(heat =>
            heat.id === id
              ? {
                ...heat,
                chemical_carbon: existingEntry.chemical_carbon || '',
                chemical_manganese: existingEntry.chemical_manganese || '',
                chemical_silicon: existingEntry.chemical_silicon || '',
                chemical_sulphur: existingEntry.chemical_sulphur || '',
                chemical_phosphorus: existingEntry.chemical_phosphorus || '',
                isLoadingChemical: false,
                chemicalAutoFetched: true,
                chemicalReadOnly: false // Allow editing
              }
              : heat
          )
        }));
        return; // Skip API call
      }

      // 2. If not found in current form, fetch from backend
      try {
        console.log(`🔬 Auto-fetching chemical analysis from database for heat number: ${heatNumber}`);
        const response = await inspectionCallService.getChemicalAnalysisByHeatNumber(heatNumber);

        if (response && response.data) {
          const analysis = response.data;
          console.log('✅ Chemical analysis found in database:', analysis);

          // Update the specific heat mapping with fetched chemical analysis
          setFormData(prev => ({
            ...prev,
            rm_heat_tc_mapping: prev.rm_heat_tc_mapping.map(heat =>
              heat.id === id
                ? {
                  ...heat,
                  chemical_carbon: analysis.carbon || '',
                  chemical_manganese: analysis.manganese || '',
                  chemical_silicon: analysis.silicon || '',
                  chemical_sulphur: analysis.sulphur || '',
                  chemical_phosphorus: analysis.phosphorus || '',
                  isLoadingChemical: false,
                  chemicalAutoFetched: true,
                  chemicalReadOnly: false // Allow editing
                }
                : heat
            )
          }));
        } else {
          console.log('ℹ️ No previous chemical analysis found for this heat number');
          // Clear loading state
          setFormData(prev => ({
            ...prev,
            rm_heat_tc_mapping: prev.rm_heat_tc_mapping.map(heat =>
              heat.id === id ? { ...heat, isLoadingChemical: false, chemicalAutoFetched: false, chemicalReadOnly: false } : heat
            )
          }));
        }
      } catch (error) {
        console.error('❌ Error fetching chemical analysis:', error);
        // Clear loading state on error
        setFormData(prev => ({
          ...prev,
          rm_heat_tc_mapping: prev.rm_heat_tc_mapping.map(heat =>
            heat.id === id ? { ...heat, isLoadingChemical: false, chemicalAutoFetched: false, chemicalReadOnly: false } : heat
          )
        }));
      }
    }
  };

  // Handle TC number selection and auto-fetch details
  const handleTcNumberChange = (id, tcNumber) => {
    const heatMapping = formData.rm_heat_tc_mapping.find(h => h.id === id);
    if (!heatMapping) return;

    // Set loading state
    setFormData(prev => ({
      ...prev,
      rm_heat_tc_mapping: prev.rm_heat_tc_mapping.map(heat =>
        heat.id === id ? { ...heat, isLoading: true, tcNumber } : heat
      )
    }));

    // Find inventory entry matching heat number and TC number
    const inventoryEntry = inventoryEntries.find(
      entry => entry.heatNumber === heatMapping.heatNumber && entry.tcNumber === tcNumber
    );

    if (inventoryEntry) {
      // Calculate total value
      const totalValue = (
        inventoryEntry.subPoQty *
        inventoryEntry.rateOfMaterial *
        (1 + inventoryEntry.rateOfGst / 100)
      ).toFixed(2);

      // Auto-fetch chemical analysis if available
      const analysis = CHEMICAL_ANALYSIS_HISTORY.find(
        a => a.heatNumber === inventoryEntry.heatNumber && a.manufacturer === inventoryEntry.supplierName
      );

      // Update the specific heat mapping with fetched data
      setFormData(prev => ({
        ...prev,
        rm_heat_tc_mapping: prev.rm_heat_tc_mapping.map(heat =>
          heat.id === id
            ? {
              ...heat,
              tcNumber,
              tcDate: inventoryEntry.tcDate || '',
              manufacturer: inventoryEntry.supplierName || '',
              invoiceNo: inventoryEntry.invoiceNumber || '',
              invoiceDate: inventoryEntry.invoiceDate || '',
              subPoNumber: inventoryEntry.subPoNumber || '',
              subPoDate: inventoryEntry.subPoDate || '',
              subPoQty: `${inventoryEntry.subPoQty} ${inventoryEntry.unitOfMeasurement}`,
              subPoTotalValue: `₹${totalValue}`,
              tcQty: `${inventoryEntry.tcQuantity} ${inventoryEntry.unitOfMeasurement}`,
              tcQtyRemaining: `${inventoryEntry.qtyLeftForInspection} ${inventoryEntry.unitOfMeasurement}`,
              maxQty: inventoryEntry.qtyLeftForInspection,
              unit: inventoryEntry.unitOfMeasurement,
              isLoading: false
            }
            : heat
        ),
        // Update chemical analysis if this is the first heat or if not already set
        rm_chemical_carbon: prev.rm_chemical_carbon || analysis?.carbon || '',
        rm_chemical_manganese: prev.rm_chemical_manganese || analysis?.manganese || '',
        rm_chemical_silicon: prev.rm_chemical_silicon || analysis?.silicon || '',
        rm_chemical_sulphur: prev.rm_chemical_sulphur || analysis?.sulphur || '',
        rm_chemical_phosphorus: prev.rm_chemical_phosphorus || analysis?.phosphorus || '',
        // rm_chemical_chromium: prev.rm_chemical_chromium || analysis?.chromium || ''
      }));
    } else {
      // No data found - clear loading state
      setFormData(prev => ({
        ...prev,
        rm_heat_tc_mapping: prev.rm_heat_tc_mapping.map(heat =>
          heat.id === id ? { ...heat, isLoading: false } : heat
        )
      }));
    }
  };

  // Handle offered quantity change for a specific heat
  const handleHeatOfferedQtyChange = (id, offeredQty) => {
    setFormData(prev => ({
      ...prev,
      rm_heat_tc_mapping: prev.rm_heat_tc_mapping.map(heat =>
        heat.id === id ? { ...heat, offeredQty } : heat
      )
    }));

    // Perform immediate validation
    const heatMapping = formData.rm_heat_tc_mapping.find(h => h.id === id);
    if (heatMapping) {
      const index = formData.rm_heat_tc_mapping.findIndex(h => h.id === id);
      const errorKey = `heat_${index}_offeredQty`;
      const numOffered = parseFloat(offeredQty);
      const maxAllowed = parseFloat(heatMapping.maxQty) || parseFloat(heatMapping.tcQtyRemaining);

      setErrors(prev => {
        const newErrors = { ...prev };
        if (isNaN(numOffered) || numOffered <= 0) {
          newErrors[errorKey] = 'Offered Quantity must be greater than 0';
        } else if (maxAllowed !== undefined && !isNaN(maxAllowed) && numOffered > maxAllowed) {
          newErrors[errorKey] = `Offered Qty cannot be more than TC Qty Remaining with Vendor (${maxAllowed} ${heatMapping.unit})`;
        } else {
          delete newErrors[errorKey];
        }
        return newErrors;
      });
    }
  };

  // Handle chemical analysis change for a specific heat
  const handleHeatChemicalChange = (id, fieldName, value) => {
    const heatMapping = formData.rm_heat_tc_mapping.find(h => h.id === id);
    if (!heatMapping) return;

    const heatNumber = heatMapping.heatNumber;

    setFormData(prev => ({
      ...prev,
      rm_heat_tc_mapping: prev.rm_heat_tc_mapping.map(heat => {
        // Sync chemical data for all rows with the same heat number
        if (heat.id === id || (heatNumber && heat.heatNumber === heatNumber)) {
          return { ...heat, [fieldName]: value };
        }
        return heat;
      })
    }));

    // Find all indices for the same heat to update errors
    formData.rm_heat_tc_mapping.forEach((h, idx) => {
      if (h.heatNumber === heatNumber || h.id === id) {
        const errorKey = `heat_${idx}_${fieldName}`;

        // Check if value is out of range and set error immediately
        if (isChemistryOutOfRange(fieldName, value)) {
          setErrors(prev => ({
            ...prev,
            [errorKey]: 'Ladle Analysis of Heat should be in tolerance of the Grade Chemistry'
          }));
        } else {
          // Clear error if value is now in range
          if (errors[errorKey]) {
            setErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors[errorKey];
              return newErrors;
            });
          }
        }
      }
    });
  };

  // Helper function to check if chemistry value is out of range
  const isChemistryOutOfRange = (fieldName, value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || value === '') return false;

    // Handle both rm_chemical_ and chemical_ prefixes
    const baseFieldName = fieldName.replace('rm_chemical_', '').replace('chemical_', '');

    switch (baseFieldName) {
      case 'carbon':
        return numValue < 0.5 || numValue > 0.6;
      case 'manganese':
        return numValue < 0.8 || numValue > 1.0;
      case 'silicon':
        return numValue < 1.5 || numValue > 2.0;
      case 'sulphur':
        return numValue > 0.03;
      case 'phosphorus':
        return numValue > 0.03;
      default:
        return false;
    }
  };

  // Generic change handler
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Special handling for heat numbers
    // if (name === 'rm_heat_numbers') {
    //   handleHeatNumbersChange(value);
    //   return;
    // }

    if (name === 'call_category') {
      setFormData(prev => ({
        ...prev,
        call_category: value,
        type_of_call: (value === 'DIVERTED' && prev.type_of_call === 'Raw Material') ? '' : prev.type_of_call,
        process_rm_ic_numbers: [],
        final_rm_ic_numbers: [],
        final_process_ic_numbers: [],
        final_lot_numbers: []
      }));
      if (errors.call_category) {
        setErrors(prev => ({ ...prev, call_category: '' }));
      }
      return;
    }

    if (name === 'type_of_erc' && (value === 'MK-III' || value === 'J-Type')) {
      setPendingErcType(value);
      setShowErcWarning(true);
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));

    // For chemistry fields, immediately show error if out of range
    if (name.startsWith('rm_chemical_')) {
      if (isChemistryOutOfRange(name, value)) {
        setErrors(prev => ({
          ...prev,
          [name]: 'Ladle Analysis of Heat should be in tolerance of the Grade Chemistry'
        }));
      } else {
        // Clear error if value is now in range
        if (errors[name]) {
          setErrors(prev => ({ ...prev, [name]: '' }));
        }
      }
    } else {
      // Clear error for non-chemistry fields when user starts typing
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
    }
  };



  const handleSubmit = () => {
    console.log('🚀 Submitting form data:', formData);
    console.log('📋 Validation starting...');

    const newErrors = {};
    // const today = getTodayDate();
    // const maxDate = getMaxDate();

    // Common validations
    if (!formData.po_serial_no) {
      newErrors.po_serial_no = 'PO Serial Number is required';
    }

    if (!formData.type_of_call) {
      newErrors.type_of_call = 'Type of Call is required';
    }

    if (!formData.type_of_erc) {
      newErrors.type_of_erc = 'Type of ERC is required';
    }

    if (!formData.desired_inspection_date) {
      newErrors.desired_inspection_date = 'Desired Inspection Date is required';
    } else if (formData.desired_inspection_date < getTodayDate()) {
      newErrors.desired_inspection_date = 'Desired Date of Inspection cannot be in the past';
    } else if (!isModifyMode && formData.desired_inspection_date > getMaxDate()) {
      newErrors.desired_inspection_date = 'Desired Date of Inspection should not be more than 6 days from today';
    }

    if (!formData.company_name) {
      newErrors.company_name = 'Company is required';
    }

    // Check unit_name for all call types (Raw Material, Process, Final)
    if (!formData.unit_name) {
      newErrors.unit_name = 'Unit is required';
    }

    // Check if POI code is available
    if (!poiCode) {
      newErrors.unit_name = 'Please select a valid company and unit to get POI code';
    }

    // Raw Material stage validations
    if (formData.type_of_call === 'Raw Material') {
      // TEMPORARY: Make RM fields optional for testing without inventory data
      // TODO: Re-enable strict validation once inventory module is implemented

      // Only validate if user has started filling heat-TC mappings
      const hasHeatData = formData.rm_heat_tc_mapping.some(heat =>
        heat.heatNumber || heat.tcNumber || heat.offeredQty
      );

      if (hasHeatData) {
        // Check each heat-TC mapping
        formData.rm_heat_tc_mapping.forEach((heat, index) => {
          if (heat.heatNumber || heat.tcNumber || heat.offeredQty) {
            if (!heat.heatNumber) {
              newErrors[`heat_${index}_heatNumber`] = 'Heat Number is required';
            }
            if (!heat.tcNumber) {
              newErrors[`heat_${index}_tcNumber`] = 'TC Number is required';
            }
            if (!heat.offeredQty || parseFloat(heat.offeredQty) <= 0) {
              newErrors[`heat_${index}_offeredQty`] = 'Offered Quantity must be greater than 0';
            } else {
              // Check if offered quantity exceeds TC qty remaining
              const offeredQty = parseFloat(heat.offeredQty);
              const tcQtyRemaining = parseFloat(heat.maxQty) || parseFloat(heat.tcQtyRemaining);
              if (tcQtyRemaining && offeredQty > tcQtyRemaining) {
                newErrors[`heat_${index}_offeredQty`] = `Offered Qty cannot be more than TC Qty Remaining with Vendor (${tcQtyRemaining} ${heat.unit})`;
              }
            }
          }
        });

        // Validate total offered quantity only if heat data is provided
        if (!formData.rm_total_offered_qty_mt || parseFloat(formData.rm_total_offered_qty_mt) <= 0) {
          newErrors.rm_total_offered_qty_mt = 'Total Offered Quantity (MT) must be greater than 0';
        }
        // Removed PO Serial Quantity validation as per user request


        // Chemical analysis validations per heat (now per-heat instead of global)
        formData.rm_heat_tc_mapping.forEach((heat, index) => {
          if (heat.heatNumber || heat.tcNumber || heat.offeredQty) {
            // Validate Carbon
            if (!heat.chemical_carbon) {
              newErrors[`heat_${index}_chemical_carbon`] = 'Carbon % is required';
            } else {
              const carbon = parseFloat(heat.chemical_carbon);
              if (carbon < 0.5 || carbon > 0.6) {
                newErrors[`heat_${index}_chemical_carbon`] = 'Ladle Analysis of Heat should be in tolerance of the Grade Chemistry';
              }
            }

            // Validate Manganese
            if (!heat.chemical_manganese) {
              newErrors[`heat_${index}_chemical_manganese`] = 'Manganese % is required';
            } else {
              const manganese = parseFloat(heat.chemical_manganese);
              if (manganese < 0.8 || manganese > 1.0) {
                newErrors[`heat_${index}_chemical_manganese`] = 'Ladle Analysis of Heat should be in tolerance of the Grade Chemistry';
              }
            }

            // Validate Silicon
            if (!heat.chemical_silicon) {
              newErrors[`heat_${index}_chemical_silicon`] = 'Silicon % is required';
            } else {
              const silicon = parseFloat(heat.chemical_silicon);
              if (silicon < 1.5 || silicon > 2.0) {
                newErrors[`heat_${index}_chemical_silicon`] = 'Ladle Analysis of Heat should be in tolerance of the Grade Chemistry';
              }
            }

            // Validate Sulphur
            if (!heat.chemical_sulphur) {
              newErrors[`heat_${index}_chemical_sulphur`] = 'Sulphur % is required';
            } else {
              const sulphur = parseFloat(heat.chemical_sulphur);
              if (sulphur > 0.03) {
                newErrors[`heat_${index}_chemical_sulphur`] = 'Ladle Analysis of Heat should be in tolerance of the Grade Chemistry';
              }
            }

            // Validate Phosphorus
            if (!heat.chemical_phosphorus) {
              newErrors[`heat_${index}_chemical_phosphorus`] = 'Phosphorus % is required';
            } else {
              const phosphorus = parseFloat(heat.chemical_phosphorus);
              if (phosphorus > 0.03) {
                newErrors[`heat_${index}_chemical_phosphorus`] = 'Ladle Analysis of Heat should be in tolerance of the Grade Chemistry';
              }
            }
          }
        });
      }
    }

    // Process stage validations
    if (formData.type_of_call === 'Process') {
      // Check if RM IC numbers are selected
      if (!formData.process_rm_ic_numbers || formData.process_rm_ic_numbers.length === 0) {
        newErrors.process_rm_ic_numbers = 'At least one RM IC Number (ER Number) is required';
      }

      // Note: company_id and unit_id can be NULL if the RM IC uses POI code instead
      // The database migration V8 made these fields nullable to support POI code-based lookups
      // So we don't validate these fields - they are optional
      if (formData.company_id) {
        console.log('✅ Company ID is populated:', formData.company_id);
      } else {
        console.log('ℹ️ Company ID is NULL - this RM IC uses POI code instead');
      }
      if (formData.unit_id) {
        console.log('✅ Unit ID is populated:', formData.unit_id);
      } else {
        console.log('ℹ️ Unit ID is NULL - this RM IC uses POI code instead');
      }

      // Check if lot-heat mappings are provided
      const hasLotHeatData = formData.process_lot_heat_mapping.some(item =>
        item.lotNumber || item.manufacturerHeat || item.offeredQty
      );

      if (hasLotHeatData) {
        // Validate each lot-heat mapping
        formData.process_lot_heat_mapping.forEach((item, index) => {
          if (item.lotNumber || item.manufacturerHeat || item.offeredQty || item.tentativeStartDate) {
            if (!item.lotNumber) {
              newErrors[`process_lot_${index}_lotNumber`] = `Lot Number is required for entry ${index + 1}`;
            }
            if (!item.manufacturerHeat) {
              newErrors[`process_lot_${index}_manufacturerHeat`] = `Manufacturer-Heat is required for entry ${index + 1}`;
            }
            if (!item.offeredQty || parseFloat(item.offeredQty) <= 0) {
              newErrors[`process_lot_${index}_offeredQty`] = `Offered Quantity must be greater than 0 for entry ${index + 1}`;
            }
            // Commented out as per requirement: open from minus infinite to infinite
            // if (!item.tentativeStartDate) {
            //   newErrors[`process_lot_${index}_tentativeStartDate`] = `Tentative Start Date is required for entry ${index + 1}`;
            // }

            // Validate that offered quantity doesn't exceed future balance
            if (item.offeredQty && item.heatNumber) {
              const offeredQty = parseInt(item.offeredQty) || 0;
              const heatSummary = heatSummaryData.find(h => h.heatNo === item.heatNumber);

              if (heatSummary) {
                // Calculate available balance for THIS lot
                // Available = Max ERC - Offered Earlier - (Total offered by OTHER lots using same heat)
                const otherLotsOffered = formData.process_lot_heat_mapping
                  .filter((lot, lotIndex) => lot.heatNumber === item.heatNumber && lotIndex !== index)
                  .reduce((sum, lot) => sum + (parseInt(lot.offeredQty) || 0), 0);

                const availableBalance = heatSummary.maxErc - (heatSummary.offeredEarlier || 0) - otherLotsOffered;

                if (offeredQty > availableBalance) {
                  newErrors[`process_lot_${index}_offeredQty`] =
                    `Offered Quantity (${offeredQty}) exceeds available Future Balance (${availableBalance}) for Heat ${item.heatNumber}`;
                }
              }
            }
          }
        });
      } else {
        newErrors.process_lot_heat_mapping = 'At least one Lot-Heat mapping is required';
      }
    }

    // Final stage validations
    if (formData.type_of_call === 'Final') {
      if (formData.call_category !== 'DIVERTED' && (!formData.final_rm_ic_numbers || formData.final_rm_ic_numbers.length === 0)) {
        newErrors.final_rm_ic_numbers = 'At least one RM IC Number is required';
      }
      if (!formData.final_process_ic_numbers || formData.final_process_ic_numbers.length === 0) {
        newErrors.final_process_ic_numbers = 'At least one Process IC Number is required';
      }
      if (!formData.final_lot_numbers || formData.final_lot_numbers.length === 0) {
        newErrors.final_lot_numbers = 'At least one Lot Number is required';
      }
      if (!formData.final_erc_qty) {
        newErrors.final_erc_qty = 'ERC Quantity is required';
      }
      if (!formData.final_hdpe_bags) {
        newErrors.final_hdpe_bags = 'HDPE Bags quantity is required';
      }
      if (!formData.final_total_qty) {
        newErrors.final_total_qty = 'Total Quantity is required';
      }

      // Validate each lot in final_lots_data
      if (formData.final_lots_data && formData.final_lots_data.length > 0) {
        formData.final_lots_data.forEach((lot, index) => {
          const maxAvailable = Math.max(0, (lot.acceptedQtyProcess || 0) - (lot.offeredEarlier || 0));
          const offeredQty = parseInt(lot.offeredQty) || 0;

          if (!lot.offeredQty || offeredQty <= 0) {
            newErrors[`final_lot_${index}_qty`] = `Offered Quantity is required for Lot ${lot.lotNumber}`;
          } else if (offeredQty > maxAvailable) {
            newErrors[`final_lot_${index}_qty`] = `Offered Quantity for Lot ${lot.lotNumber} cannot exceed available quantity (${maxAvailable})`;
          }

          if (!lot.noOfBags || parseInt(lot.noOfBags) <= 0) {
            newErrors[`final_lot_${index}_bags`] = `No. of Bags is required for Lot ${lot.lotNumber}`;
          }
        });
      }
    }

    console.log('🔍 Validation errors:', newErrors);

    if (Object.keys(newErrors).length > 0) {
      console.log('❌ Validation failed!', newErrors);
      setErrors(newErrors);

      // Scroll to the first error field
      const firstErrorField = Object.keys(newErrors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`) ||
        document.querySelector(`.ric-form-error`);

      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Focus on the field if it's an input
        if (errorElement.tagName === 'INPUT' || errorElement.tagName === 'SELECT' || errorElement.tagName === 'TEXTAREA') {
          setTimeout(() => errorElement.focus(), 300);
        }
      }

      // Show validation summary alert
      setNotification({
        message: `❌ Form Validation Failed\n\nPlease fix the errors highlighted below.`,
        type: 'error'
      });

      return;
    }

    console.log('✅ Validation passed!');
    setErrors({});

    console.log('🔍 DEBUG: formData.type_of_erc =', formData.type_of_erc);

    // Filter data based on inspection type - send only relevant fields
    // Convert company_id and unit_id to proper types (null or integer)
    const companyId = formData.company_id ? (typeof formData.company_id === 'number' ? formData.company_id : parseInt(formData.company_id)) : null;
    const unitId = formData.unit_id ? (typeof formData.unit_id === 'number' ? formData.unit_id : parseInt(formData.unit_id)) : null;

    console.log('🔍 Type conversion check:');
    console.log('  formData.company_id:', formData.company_id, 'Type:', typeof formData.company_id);
    console.log('  Converted companyId:', companyId, 'Type:', typeof companyId);
    console.log('  formData.unit_id:', formData.unit_id, 'Type:', typeof formData.unit_id);
    console.log('  Converted unitId:', unitId, 'Type:', typeof unitId);

    let filteredData = {
      // Common fields for all inspection types
      po_no: formData.po_no,
      po_serial_no: formData.po_serial_no,
      po_date: formData.po_date,
      po_description: formData.po_description,
      po_qty: formData.po_qty,
      po_unit: formData.po_unit,
      call_nature: formData.call_category || 'FRESH',
      callNature: formData.call_category || 'FRESH',
      is_diverted_material: formData.call_category === 'DIVERTED',
      isDivertedMaterial: formData.call_category === 'DIVERTED',
      source_ic_no: formData.call_category === 'DIVERTED'
        ? (formData.process_rm_ic_numbers?.[0] || formData.final_process_ic_numbers?.[0] || formData.final_rm_ic_numbers?.[0] || null)
        : null,
      type_of_call: formData.type_of_call,
      type_of_erc: formData.type_of_erc,
      desired_inspection_date: formData.desired_inspection_date,
      company_id: companyId, // Use converted value (fetched from RM IC for Process)
      company_name: formData.company_name,
      unit_id: unitId, // Use converted value (fetched from RM IC for Process)
      unit_name: formData.unit_name,
      unit_address: formData.unit_address,
      placeOfInspection: poiCode, // POI code from API
      remarks: formData.remarks
    };

    // Add type-specific fields
    if (formData.type_of_call === 'Raw Material') {
      // Transform heat-tc mapping to include chemical analysis per heat
      const heatQuantities = formData.rm_heat_tc_mapping.map(heat => ({
        heatNumber: heat.heatNumber,
        manufacturer: heat.manufacturer,
        offeredQty: parseFloat(heat.offeredQty) || 0,
        tcNumber: heat.tcNumber,
        tcDate: heat.tcDate,
        tcQuantity: parseFloat(heat.tcQty) || 0,
        qtyLeft: parseFloat(heat.tcQtyRemaining) || 0
      }));

      const chemicalAnalysis = formData.rm_heat_tc_mapping.map(heat => ({
        heatNumber: heat.heatNumber,
        carbon: heat.chemical_carbon ? parseFloat(heat.chemical_carbon) : null,
        manganese: heat.chemical_manganese ? parseFloat(heat.chemical_manganese) : null,
        silicon: heat.chemical_silicon ? parseFloat(heat.chemical_silicon) : null,
        sulphur: heat.chemical_sulphur ? parseFloat(heat.chemical_sulphur) : null,
        phosphorus: heat.chemical_phosphorus ? parseFloat(heat.chemical_phosphorus) : null
      }));

      filteredData = {
        ...filteredData,
        rm_heat_tc_mapping: formData.rm_heat_tc_mapping,
        heatQuantities: heatQuantities,
        chemicalAnalysis: chemicalAnalysis,
        rm_total_offered_qty_mt: formData.rm_total_offered_qty_mt,
        rm_offered_qty_erc: formData.rm_offered_qty_erc,
        rm_remarks: formData.remarks
      };
    } else if (formData.type_of_call === 'Process') {
      filteredData = {
        ...filteredData,
        process_rm_ic_numbers: formData.process_rm_ic_numbers,
        process_book_set_nos: formData.process_book_set_nos,
        process_lot_heat_mapping: formData.process_lot_heat_mapping,
        process_total_accepted_qty_rm: formData.process_total_accepted_qty_rm
      };
    } else if (formData.type_of_call === 'Final') {
      filteredData = {
        ...filteredData,
        final_lot_numbers: formData.final_lot_numbers,
        final_manufacturer_heat: formData.final_manufacturer_heat,
        final_erc_qty: formData.final_erc_qty,
        final_total_qty: formData.final_total_qty,
        final_hdpe_bags: formData.final_hdpe_bags,
        final_rm_ic_numbers: formData.final_rm_ic_numbers,
        final_process_ic_numbers: formData.final_process_ic_numbers,
        final_total_accepted_qty_process: formData.final_total_accepted_qty_process,
        final_lots_data: formData.final_lots_data
        // Note: unit_id, unit_name, unit_address are already in filteredData from common fields
      };
    }

    console.log('📤 Calling onSubmit with filtered data:', filteredData);
    console.log('🏢 POI Code being sent:', poiCode);
    console.log('🏭 Company ID:', filteredData.company_id);
    console.log('🏭 Company Name:', filteredData.company_name);
    console.log('🏗️ Unit ID:', filteredData.unit_id);
    console.log('🏗️ Unit Name:', filteredData.unit_name);
    console.log('📍 Full formData.company_id:', formData.company_id);
    console.log('📍 Full formData.unit_id:', formData.unit_id);
    onSubmit(filteredData);
  };

  const handleReset = () => {
    setFormData(getInitialFormState(selectedPO, selectedItem));
    setSelectedPoSerial('');
    setErrors({});
  };

  return (
    <div className="ric-form">
      {/* ============ VALIDATION ERROR SUMMARY ============ */}
      {Object.keys(errors).length > 0 && !isViewMode && (
        <div className="ric-validation-summary">
          <div className="ric-validation-summary__header">
            <span className="ric-validation-summary__icon">⚠️</span>
            <h4 className="ric-validation-summary__title">
              Please fix {Object.keys(errors).length} error{Object.keys(errors).length > 1 ? 's' : ''} before submitting
            </h4>
          </div>
          <ul className="ric-validation-summary__list">
            {Object.entries(errors).slice(0, 10).map(([field, message]) => (
              <li key={field} className="ric-validation-summary__item">
                <button
                  type="button"
                  className="ric-validation-summary__link"
                  onClick={() => {
                    const element = document.querySelector(`[name="${field}"]`) ||
                      document.querySelector(`.ric-form-error`);
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      if (element.tagName === 'INPUT' || element.tagName === 'SELECT') {
                        setTimeout(() => element.focus(), 300);
                      }
                    }
                  }}
                >
                  {message}
                </button>
              </li>
            ))}
            {Object.keys(errors).length > 10 && (
              <li className="ric-validation-summary__item ric-validation-summary__item--more">
                ... and {Object.keys(errors).length - 10} more errors
              </li>
            )}
          </ul>
        </div>
      )}

      <fieldset disabled={isViewMode} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
        {/* ============ COMMON SECTION ============ */}
        <SectionHeader
          title="PO Data (Auto Fetched from IREPS)"
          subtitle="Select PO Serial Number to auto-populate PO details"
        />

        <div className="ric-form-grid">
          <FormField label="PO Serial Number" name="po_serial_no" required hint={selectedItem ? "Auto Fetched" : "Select to auto-fetch PO data"} errors={errors}>
            {selectedItem ? (
              <input
                type="text"
                className="ric-form-input ric-form-input--disabled"
                value={selectedPoSerial}
                disabled
              />
            ) : (
              <select
                className="ric-form-select"
                value={selectedPoSerial}
                onChange={(e) => handlePoSerialChange(e.target.value)}
              >
                <option value="">Select PO Serial Number</option>
                {poSerialOptions.map(po => (
                  <option key={po.serialNo} value={po.serialNo}>
                    {po.serialNo} - {po.itemName}
                  </option>
                ))}
              </select>
            )}
          </FormField>

          <FormField label="Zone Name" name="zone_name" hint="Auto Fetched" errors={errors}>
            <input type="text" className="ric-form-input ric-form-input--disabled" value={formData.zone_name} disabled />
          </FormField>

          <FormField label="PO Number" name="po_no" hint="Auto Fetched" errors={errors}>
            <input type="text" className="ric-form-input ric-form-input--disabled" value={formData.po_no} disabled />
          </FormField>

          <FormField label="PO Date" name="po_date" hint="Auto Fetched" errors={errors}>
            <input type="text" className="ric-form-input ric-form-input--disabled" value={formData.po_date ? formatDate(formData.po_date) : ''} disabled />
          </FormField>

          <FormField label="PO Sr No Qty" name="po_qty" hint="Auto Fetched" errors={errors}>
            <input type="text" className="ric-form-input ric-form-input--disabled" value={`${formData.po_qty} ${formData.po_unit}`} disabled />
          </FormField>

          {formData.amendment_no && (
            <>
              <FormField label="Amendment No." name="amendment_no" hint="Auto Fetched" errors={errors}>
                <input type="text" className="ric-form-input ric-form-input--disabled" value={formData.amendment_no} disabled />
              </FormField>
              <FormField label="Amendment Date" name="amendment_date" hint="Auto Fetched" errors={errors}>
                <input type="text" className="ric-form-input ric-form-input--disabled" value={formatDate(formData.amendment_date)} disabled />
              </FormField>
            </>
          )}

          <FormField label="Type of ERC" name="type_of_erc" required hint="Select ERC type" errors={errors}>
            <select
              name="type_of_erc"
              className="ric-form-select"
              value={formData.type_of_erc}
              onChange={handleChange}
            >
              {ERC_TYPES.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </FormField>

          <FormField label="Desired Inspection Date" name="desired_inspection_date" required hint="Maximum 6 days from today" errors={errors}>
            <input
              type="date"
              name="desired_inspection_date"
              className="ric-form-input"
              value={formData.desired_inspection_date}
              onChange={handleChange}
              min={getTodayDate()}
              max={isModifyMode ? undefined : getMaxDate()}
            />
          </FormField>


        </div>

        {/* Quantity Already Inspected Summary */}
        {formData.po_serial_no && (
          <div className="ric-qty-summary">
            <div className="ric-qty-summary__title">Quantity Accepted</div>
            <div className="ric-qty-summary__grid">
              <div className="ric-qty-card">
                <span className="ric-qty-card__label">Raw Material</span>
                <span className="ric-qty-card__value">{formData.qty_already_inspected_rm}</span>
              </div>
              <div className="ric-qty-card">
                <span className="ric-qty-card__label">Process</span>
                <span className="ric-qty-card__value">{formData.qty_already_inspected_process}</span>
              </div>
              <div className="ric-qty-card">
                <span className="ric-qty-card__label">Final</span>
                <span className="ric-qty-card__value">{formData.qty_already_inspected_final}</span>
              </div>
              {/* <div className="ric-qty-card ric-qty-card--remaining">
              <span className="ric-qty-card__label">Remaining (PO)</span>
              <span className="ric-qty-card__value">{formData.po_qty - formData.qty_already_inspected_final}</span>
            </div> */}
            </div>
          </div>
        )}

        {/* ============ SUB PO INFORMATION (if selected) ============ */}
        {selectedSubPO && (formData.type_of_call === 'Raw Material' || formData.type_of_call === 'Process') && (
          <>
            <SectionHeader title="Sub PO Information" subtitle="Details of selected Sub PO" />
            <div className="ric-form-grid">
              <FormField label="Sub-PO Number">
                <input
                  type="text"
                  className="ric-form-input ric-form-input--disabled"
                  value={selectedSubPO.sub_po_number}
                  disabled
                />
              </FormField>
              <FormField label="Raw Material Name">
                <input
                  type="text"
                  className="ric-form-input ric-form-input--disabled"
                  value={selectedSubPO.raw_material_name}
                  disabled
                />
              </FormField>
              <FormField label="Contractor">
                <input
                  type="text"
                  className="ric-form-input ric-form-input--disabled"
                  value={selectedSubPO.contractor}
                  disabled
                />
              </FormField>
              <FormField label="Manufacturer">
                <input
                  type="text"
                  className="ric-form-input ric-form-input--disabled"
                  value={selectedSubPO.manufacturer}
                  disabled
                />
              </FormField>
              <FormField label="Sub-PO Quantity">
                <input
                  type="text"
                  className="ric-form-input ric-form-input--disabled"
                  value={selectedSubPO.sub_po_quantity}
                  disabled
                />
              </FormField>
              <FormField label="Rate (₹)">
                <input
                  type="text"
                  className="ric-form-input ric-form-input--disabled"
                  value={selectedSubPO.rate}
                  disabled
                />
              </FormField>
            </div>
          </>
        )}

        {/* ============ CALL TYPE SELECTION ============ */}
        <SectionHeader title="Call Details" subtitle="Select nature and type of inspection call" />

        <div className="ric-form-grid">
          <FormField label="Call Nature" name="call_category" required hint="Select Fresh or Diverted Call" errors={errors}>
            <select
              name="call_category"
              className="ric-form-select"
              value={formData.call_category || 'FRESH'}
              onChange={handleChange}
              disabled={isViewMode}
            >
              {CALL_NATURE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </FormField>

          <FormField label="Type of Call" name="type_of_call" required hint="Select inspection stage" errors={errors}>
            <select
              name="type_of_call"
              className="ric-form-select"
              value={formData.type_of_call}
              onChange={handleChange}
              disabled={isViewMode}
            >
              {INSPECTION_STAGES.filter(opt => {
                if (formData.call_category === 'DIVERTED' && opt.value === 'Raw Material') {
                  return false;
                }
                return true;
              }).map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </FormField>
        </div>

        {/* ── Inline warning when selected call type already has an active call ── */}
        {formData.type_of_call && !isModifyMode && blockedCallTypes.has(formData.type_of_call) && (
          <div style={{ margin: '0 0 16px', padding: '12px 16px', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '8px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <span style={{ fontSize: '18px', flexShrink: 0 }}>⚠️</span>
            <div>
              <div style={{ fontWeight: 700, color: '#92400e', fontSize: '13px' }}>{formData.type_of_call} Call Awaiting Verification / Scheduling</div>
              <div style={{ color: '#78350f', fontSize: '12px', marginTop: '4px' }}>
                A {formData.type_of_call} inspection call for this PO Serial No. is currently under review or awaiting scheduling. You can raise a new {formData.type_of_call} call once the existing call proceeds to inspection, is cancelled, or withdrawn.
              </div>
            </div>
          </div>
        )}

        {/* ============ SHOW REST OF FORM ONLY AFTER TYPE SELECTION ============ */}
        {formData.type_of_call && (isModifyMode || !blockedCallTypes.has(formData.type_of_call)) && (
          <>
            <div className="ric-form-grid">
              {/* <FormField label="Desired Inspection Date" name="desired_inspection_date" required hint="" errors={errors}>
              <input
                type="date"
                name="desired_inspection_date"
                className="ric-form-input"
                value={formData.desired_inspection_date}
                onChange={handleChange}
                // min={getTodayDate()}
                // max={getMaxDate()}
              />
            </FormField> */}

              {/* <FormField label="Vendor Contact Name" name="vendor_contact_name">
              <input
                type="text"
                name="vendor_contact_name"
                className="ric-form-input"
                value={formData.vendor_contact_name}
                onChange={handleChange}
                placeholder="Enter contact person name"
              />
            </FormField> */}

              {/* <FormField label="Vendor Contact Phone" name="vendor_contact_phone">
              <input
                type="tel"
                name="vendor_contact_phone"
                className="ric-form-input"
                value={formData.vendor_contact_phone}
                onChange={handleChange}
                placeholder="Enter phone number"
              />
            </FormField> */}
            </div>

            {/* ============ RAW MATERIAL STAGE FIELDS ============ */}
            {formData.type_of_call === 'Raw Material' && (
              <>
                <SectionHeader
                  title="ERC Raw Material Details - Heat Numbers & TC Information"
                  subtitle="Add multiple heat numbers and select TC for each. Details will be auto-fetched from inventory."
                />

                {/* Dynamic Heat-TC Mapping Sections */}
                {formData.rm_heat_tc_mapping.map((heatMapping, index) => (
                  <div key={heatMapping.id} className="ric-heat-section">
                    <div className="ric-heat-section-header">
                      <h4 className="ric-heat-section-title">Heat Number {index + 1}</h4>
                      {formData.rm_heat_tc_mapping.length > 1 && !isViewMode && (
                        <button
                          type="button"
                          className="ric-btn-remove-heat"
                          onClick={() => handleRemoveHeatNumber(heatMapping.id)}
                          title="Remove this heat number"
                        >
                          ✕ Remove
                        </button>
                      )}
                    </div>

                    <div className="ric-form-grid">
                      {/* Heat Number - Dropdown from Inventory {heat.rawMaterial} - Qty Left: {heat.qtyLeft} {heat.unit}*/}
                      <FormField
                        label="Heat Number"
                        name={`heat_${index}_heatNumber`}
                        required
                        errors={errors}
                      >
                        <select
                          className="ric-form-select"
                          value={heatMapping.compositeKey || ''}
                          onChange={(e) => {
                            const compositeKey = e.target.value;
                            if (compositeKey) {
                              // Parse composite key: "heatNumber|supplierName"
                              const [heatNum, supplier] = compositeKey.split('|');
                              const selectedHeat = getAvailableHeatNumbersForDropdown(heatMapping.id).find(
                                h => h.heatNumber === heatNum && h.supplierName === supplier
                              );
                              if (selectedHeat) {
                                handleHeatNumberChange(heatMapping.id, selectedHeat.heatNumber, selectedHeat.supplierName, compositeKey);
                              }
                            } else {
                              handleHeatNumberChange(heatMapping.id, '', '', '');
                            }
                          }}
                        >
                          <option value="">-- Select Heat Number --</option>
                          {getAvailableHeatNumbersForDropdown(heatMapping.id).map(heat => {
                            const compositeKey = `${heat.heatNumber}|${heat.supplierName}`;
                            return (
                              <option key={compositeKey} value={compositeKey}>
                                {heat.heatNumber} -  ({heat.supplierName})
                              </option>
                            );
                          })}
                        </select>
                      </FormField>

                      {/* TC Number - Dropdown based on selected Heat Number */}
                      <FormField
                        label="TC (Test Certificate) Number"
                        name={`heat_${index}_tcNumber`}
                        required
                        hint="Auto-fetched from inventory"
                        errors={errors}
                      >
                        <select
                          className="ric-form-select"
                          value={heatMapping.tcNumber}
                          onChange={(e) => handleTcNumberChange(heatMapping.id, e.target.value)}
                          disabled={!heatMapping.heatNumber}
                        >
                          <option value="">Select TC Number</option>
                          {getAvailableTcNumbers(heatMapping.heatNumber, heatMapping.supplierName, heatMapping.id).map(tc => (
                            <option key={tc.tcNumber} value={tc.tcNumber}>
                              {tc.tcNumber} - {tc.manufacturer}
                            </option>
                          ))}
                        </select>
                        {heatMapping.isLoading && (
                          <div style={{ marginTop: '4px', fontSize: '12px', color: '#2196f3' }}>
                            Loading details...
                          </div>
                        )}
                      </FormField>

                      {/* TC Date - Auto-fetched */}
                      <FormField label="TC Date" name={`heat_${index}_tcDate`} hint="Auto-fetched">
                        <input
                          type="text"
                          className="ric-form-input ric-form-input--disabled"
                          value={heatMapping.tcDate ? formatDate(heatMapping.tcDate) : ''}
                          disabled
                        />
                      </FormField>

                      {/* Manufacturer - Auto-fetched */}
                      <FormField label="Manufacturer Name" name={`heat_${index}_manufacturer`} hint="Auto-fetched">
                        <input
                          type="text"
                          className="ric-form-input ric-form-input--disabled"
                          value={heatMapping.manufacturer}
                          disabled
                        />
                      </FormField>

                      {/* Invoice Number - Auto-fetched */}
                      <FormField label="Invoice Number" name={`heat_${index}_invoiceNo`} hint="Auto-fetched">
                        <input
                          type="text"
                          className="ric-form-input ric-form-input--disabled"
                          value={heatMapping.invoiceNo}
                          disabled
                        />
                      </FormField>

                      {/* Invoice Date - Auto-fetched */}
                      <FormField label="Invoice Date" name={`heat_${index}_invoiceDate`} hint="Auto-fetched">
                        <input
                          type="text"
                          className="ric-form-input ric-form-input--disabled"
                          value={heatMapping.invoiceDate ? formatDate(heatMapping.invoiceDate) : ''}
                          disabled
                        />
                      </FormField>

                      {/* Sub PO Number & Date - Auto-fetched */}
                      <FormField label="Sub PO Number & Date" name={`heat_${index}_subPoNumber`} hint="Auto-fetched">
                        <input
                          type="text"
                          className="ric-form-input ric-form-input--disabled"
                          value={heatMapping.subPoNumber && heatMapping.subPoDate
                            ? `${heatMapping.subPoNumber} (${formatDate(heatMapping.subPoDate)})`
                            : ''}
                          disabled
                        />
                      </FormField>

                      {/* Sub PO Qty - Auto-fetched */}
                      <FormField label="Sub PO Qty" name={`heat_${index}_subPoQty`} hint="Auto-fetched">
                        <input
                          type="text"
                          className="ric-form-input ric-form-input--disabled"
                          value={heatMapping.subPoQty}
                          disabled
                        />
                      </FormField>

                      {/* Total Value of Sub PO - Auto-fetched */}
                      {!isViewMode && (
                        <FormField label="Total Value of Sub PO" name={`heat_${index}_subPoTotalValue`} hint="Auto-fetched">
                          <input
                            type="text"
                            className="ric-form-input ric-form-input--disabled"
                            value={heatMapping.subPoTotalValue}
                            disabled
                          />
                        </FormField>
                      )}

                      {/* TC Qty - Auto-fetched */}
                      <FormField label="TC Qty" name={`heat_${index}_tcQty`} hint="Auto-fetched">
                        <input
                          type="text"
                          className="ric-form-input ric-form-input--disabled"
                          value={heatMapping.tcQty}
                          disabled
                        />
                      </FormField>

                      {/* TC Qty Remaining with Vendor - Auto-fetched */}
                      {!isViewMode && (
                        <FormField label="TC Qty Remaining with Vendor" name={`heat_${index}_tcQtyRemaining`} hint="Auto-fetched">
                          <input
                            type="text"
                            className="ric-form-input ric-form-input--disabled"
                            value={heatMapping.tcQtyRemaining}
                            disabled
                          />
                        </FormField>
                      )}

                      {/* Offered Quantity for this Heat - Manual Input */}
                      <FormField
                        label="Offered Qty (MT)"
                        name={`heat_${index}_offeredQty`}
                        required
                        hint={heatMapping.maxQty !== undefined && heatMapping.maxQty !== '' ? `Max: ${heatMapping.maxQty} MT (Original Limit)` : heatMapping.tcQtyRemaining ? `Max: ${heatMapping.tcQtyRemaining} MT (TC Qty Remaining)` : ''}
                        errors={errors}
                      >
                        <input
                          type="number"
                          className="ric-form-input"
                          value={heatMapping.offeredQty}
                          onChange={(e) => handleHeatOfferedQtyChange(heatMapping.id, e.target.value)}
                          step="0.001"
                          min="0"
                          max={heatMapping.maxQty !== undefined && heatMapping.maxQty !== '' ? heatMapping.maxQty : heatMapping.tcQtyRemaining || undefined}
                          placeholder="Enter quantity in MT"
                          disabled={!heatMapping.tcNumber}
                        />
                      </FormField>

                      {/* Max ERC Calculation - Auto-calculated based on Offered Qty and ERC Type */}
                      <FormField
                        label="Max ERC can be manufactured from this Manufacturer - Heat No. combination for this PO Sr. No."
                        name={`heat_${index}_maxErc`}
                        hint={formData.type_of_erc ? `Formula: (Offered Qty MT × 1000) / Division Factor. ${formData.type_of_erc.toUpperCase().includes('MK-III') ? 'MK-III: 0.91' : formData.type_of_erc.toUpperCase().includes('J') ? 'J-Type: 0.915' : 'MK-V: 1.14'}` : 'Select ERC type to calculate'}
                        fullWidth
                      >
                        <input
                          type="text"
                          className="ric-form-input ric-form-input--calculated"
                          value={
                            heatMapping.offeredQty && formData.type_of_erc
                              ? `${formatNumber(calculateMaxErcForHeat(heatMapping.offeredQty, formData.type_of_erc))} ERCs`
                              : '0 ERCs'
                          }
                          disabled
                          readOnly
                        />
                      </FormField>
                    </div>

                    {/* Chemical Analysis for this Heat Number */}
                    <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e0e0e0' }}>
                      <SectionHeader
                        title={`Chemical Analysis of TC - Heat #${index + 1}`}
                        subtitle={`Enter chemical composition percentages for ${heatMapping.heatNumber || 'this heat'}`}
                      />

                      {/* Loading indicator for chemical analysis */}
                      {heatMapping.isLoadingChemical && (
                        <div style={{
                          marginBottom: '16px',
                          padding: '12px',
                          backgroundColor: '#e3f2fd',
                          borderLeft: '4px solid #2196f3',
                          borderRadius: '4px',
                          fontSize: '14px',
                          color: '#1976d2'
                        }}>
                          🔬 Fetching chemical analysis from previous inspection calls...
                        </div>
                      )}

                      {/* Auto-fetched indicator */}
                      {heatMapping.chemicalAutoFetched && !heatMapping.isLoadingChemical && (
                        <div style={{
                          marginBottom: '16px',
                          padding: '12px',
                          backgroundColor: '#e8f5e9',
                          borderLeft: '4px solid #4caf50',
                          borderRadius: '4px',
                          fontSize: '14px',
                          color: '#2e7d32',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <span>✅</span>
                          <span>
                            <strong>Auto-populated:</strong> Chemical analysis data has been automatically filled based on previous entries or database records for this heat number. You can edit these values if needed.
                          </span>
                        </div>
                      )}

                      <div className="ric-form-grid">
                        <FormField label="Carbon (C) %" name={`heat_${index}_chemical_carbon`} required hint="Range: 0.5 - 0.6" errors={errors}>
                          <input
                            type="number"
                            className="ric-form-input"
                            value={heatMapping.chemical_carbon}
                            onChange={(e) => handleHeatChemicalChange(heatMapping.id, 'chemical_carbon', e.target.value)}
                            step="0.001"
                            min="0"
                            max="100"
                            placeholder="e.g., 0.55"
                          />
                        </FormField>

                        <FormField label="Manganese (Mn) %" name={`heat_${index}_chemical_manganese`} required hint="Range: 0.8 - 1.0" errors={errors}>
                          <input
                            type="number"
                            className="ric-form-input"
                            value={heatMapping.chemical_manganese}
                            onChange={(e) => handleHeatChemicalChange(heatMapping.id, 'chemical_manganese', e.target.value)}
                            step="0.001"
                            min="0"
                            max="100"
                            placeholder="e.g., 0.9"
                          />
                        </FormField>

                        <FormField label="Silicon (Si) %" name={`heat_${index}_chemical_silicon`} required hint="Range: 1.5 - 2.0" errors={errors}>
                          <input
                            type="number"
                            className="ric-form-input"
                            value={heatMapping.chemical_silicon}
                            onChange={(e) => handleHeatChemicalChange(heatMapping.id, 'chemical_silicon', e.target.value)}
                            step="0.001"
                            min="0"
                            max="100"
                            placeholder="e.g., 1.75"
                          />
                        </FormField>

                        <FormField label="Sulphur (S) %" name={`heat_${index}_chemical_sulphur`} required hint="Max: 0.03" errors={errors}>
                          <input
                            type="number"
                            className="ric-form-input"
                            value={heatMapping.chemical_sulphur}
                            onChange={(e) => handleHeatChemicalChange(heatMapping.id, 'chemical_sulphur', e.target.value)}
                            step="0.001"
                            min="0"
                            max="100"
                            placeholder="e.g., 0.02"
                          />
                        </FormField>

                        <FormField label="Phosphorus (P) %" name={`heat_${index}_chemical_phosphorus`} required hint="Max: 0.03" errors={errors}>
                          <input
                            type="number"
                            className="ric-form-input"
                            value={heatMapping.chemical_phosphorus}
                            onChange={(e) => handleHeatChemicalChange(heatMapping.id, 'chemical_phosphorus', e.target.value)}
                            step="0.001"
                            min="0"
                            max="100"
                            placeholder="e.g., 0.02"
                          />
                        </FormField>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Add Heat Number Button */}
                {!isViewMode && (
                  <div style={{ marginBottom: '24px' }}>
                    <button
                      type="button"
                      className="ric-btn-add-heat"
                      onClick={handleAddHeatNumber}
                    >
                      + Add Another Heat Number
                    </button>
                  </div>
                )}

                {/* Total Offered Quantity - Auto-calculated from all heats */}
                <SectionHeader
                  title="Total Offered Quantity Summary"
                  subtitle="Auto-calculated from all heat numbers"
                />
                <div className="ric-form-grid">
                  <FormField
                    label="Total Offered Qty (MT)"
                    name="rm_total_offered_qty_mt"
                  // hint={`Auto-calculated (Sum of all heats) | Max: ${remainingQty.rm} MT`}
                  >
                    <input
                      type="text"
                      className="ric-form-input ric-form-input--calculated"
                      value={(Number(formData.rm_total_offered_qty_mt) || 0).toFixed(3)}
                      disabled
                    />
                  </FormField>

                  <FormField
                    label="Approx. No. of ERC to be Supplied"
                    name="rm_offered_qty_erc"
                    // hint="Auto-calculated from Total Qty (1.150 MT per 1000 ERCs)"
                    hint={`Formula: (Total Offered Qty MT × 1000) / Division Factor
                        MK-V: Division Factor = 1.14
                        MK-III: Division Factor = 0.91
                        J-Type: Division Factor = 0.915${formData.type_of_erc ? ` | Current: ${formData.type_of_erc}` : ''}`}
                  >
                    <input
                      type="text"
                      className="ric-form-input ric-form-input--calculated"
                      value={(Number(formData.rm_offered_qty_erc) || 0).toLocaleString('en-IN')}
                      disabled
                    />
                  </FormField>
                </div>
              </>
            )}

            {/* ============ PROCESS STAGE FIELDS ============ */}
            {formData.type_of_call === 'Process' && (
              <>
                <SectionHeader
                  title="ERC Process Inspection Details"
                  subtitle="Select RM ICs and enter lot details"
                />

                <div className="ric-form-grid">
                  {/* RM IC Numbers - Dropdown with Multiple Selection - HARDCODED FOR TESTING { value: 'RM-IC-1767597604003', label: 'RM-IC-1767597604003 ' },
                      { value: 'RM-IC-1767603751862', label: 'RM-IC-1767603751862' },
                      { value: 'RM-IC-1767604183531', label: 'RM-IC-1767604183531' },  
                      { value: 'RM-IC-1767604882477', label: 'RM-IC-1767604882477' }*/}
                  <FormField
                    label="RM IC Numbers"
                    name="process_rm_ic_numbers"
                    required
                    hint={
                      loadingRMICs
                        ? "Loading completed RM ICs..."
                        : (formData.call_category !== 'DIVERTED' && completeDiversionSrNos.has(formData.po_serial_no))
                          ? "RM ICs not available — material for this PO SR no has been completely diverted"
                          : formData.call_category === 'DIVERTED'
                            ? "Select certificate number for diverted RM inspections"
                            : "Select certificate number for completed RM inspections"
                    }
                    fullWidth
                  >
                    <MultiSelectDropdown
                      options={approvedRMICsForProcess.map(ic => ({
                        value: ic.certificate_no,  // Store certificate number
                        label: ic.label || ic.certificate_no   // Display certificate number with date
                      }))}
                      selectedValues={formData.process_rm_ic_numbers}
                      onChange={(selectedValues) => handleRmIcSelection(selectedValues)}
                      placeholder={
                        loadingRMICs
                          ? "Loading..."
                          : (formData.call_category !== 'DIVERTED' && completeDiversionSrNos.has(formData.po_serial_no))
                            ? "Not available — material completely diverted"
                            : formData.call_category === 'DIVERTED'
                              ? "Select Diverted RM IC Certificate Numbers"
                              : "Select Certificate Numbers"
                      }
                      disabled={isViewMode || (formData.call_category !== 'DIVERTED' && completeDiversionSrNos.has(formData.po_serial_no))}
                    />
                    {formData.process_rm_ic_numbers.length > 0 && (
                      <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                        Selected: {formData.process_rm_ic_numbers.join(', ')}
                      </div>
                    )}
                  </FormField>

                  {/* Book No - Set No - Auto-fetched */}
                  <FormField
                    label="Book No - Set No."
                    name="process_book_set_nos"
                    // hint="Auto-fetched for each RM IC Number added above"
                    fullWidth
                  >
                    <div className="ric-form-input ric-form-input--disabled" style={{ minHeight: '60px', padding: '12px' }}>
                      {formData.process_book_set_nos.length > 0 ? (
                        formData.process_book_set_nos.map((item, idx) => (
                          <div key={idx} style={{ marginBottom: '4px' }}>
                            <strong>{item.icNumber}:</strong> {item.bookSetNo}
                          </div>
                        ))
                      ) : (
                        <span style={{ color: '#999' }}>Select RM IC Numbers to auto-fetch Book/Set Numbers</span>
                      )}
                    </div>
                  </FormField>

                  {/* Info Message for Multiple Lots */}
                  {formData.process_lot_heat_mapping.length > 1 && (
                    <div className="ric-info-message" style={{ gridColumn: '1 / -1', padding: '12px', backgroundColor: '#e3f2fd', borderLeft: '4px solid #2196f3', marginBottom: '16px' }}>
                      <strong>Note:</strong> If more than 1 lot no. is added - "Process IC for all the Lots will be provided once all the lots are manufactured and inspected during process inspection"
                    </div>
                  )}
                </div>

                {/* ========== HEAT SUMMARY SECTION ========== */}
                {formData.process_rm_ic_numbers.length > 0 && (
                  <div style={{ gridColumn: '1 / -1', marginTop: '24px' }}>
                    <HeatSummaryTable
                      data={heatSummaryData}
                      loading={loadingHeatSummary}
                      poSerialNo={formData.po_serial_no}
                    />
                  </div>
                )}

                {/* ========== LOT-HEAT MAPPING SECTION ========== */}
                <div style={{ gridColumn: '1 / -1', marginTop: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                      Lot Numbers & Heat Numbers
                    </h4>
                    {!isViewMode && (
                      <button
                        type="button"
                        onClick={handleAddProcessLotHeat}
                        className="ric-btn-secondary"
                        style={{
                          padding: '8px 16px',
                          fontSize: '14px',
                          backgroundColor: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        + Add Lot & Heat
                      </button>
                    )}
                  </div>

                  {/* Lot-Heat Entries */}
                  {formData.process_lot_heat_mapping.map((lotHeat, index) => (
                    <div
                      key={lotHeat.id}
                      style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '16px',
                        backgroundColor: '#f9fafb'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <h5 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                          Entry #{index + 1}
                        </h5>
                        {formData.process_lot_heat_mapping.length > 1 && !isViewMode && (
                          <button
                            type="button"
                            onClick={() => handleRemoveProcessLotHeat(lotHeat.id)}
                            style={{
                              padding: '4px 12px',
                              fontSize: '12px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className="ric-form-grid">
                        {/* Lot Number */}
                        <FormField
                          label="Lot No."
                          name={`process_lot_no_${lotHeat.id}`}
                          required
                          hint="Manual Entry - String"
                        >
                          <input
                            type="text"
                            className="ric-form-input"
                            value={lotHeat.lotNumber}
                            onChange={(e) => handleProcessLotNumberChange(lotHeat.id, e.target.value)}
                            placeholder="Enter Lot Number (e.g., LOT-2025-001)"
                          />
                        </FormField>

                        {/* Heat Number */}
                        <FormField
                          label="Heat Number"
                          name={`process_heat_number_${lotHeat.id}`}
                          required
                          hint={loadingHeats ? "Loading heat numbers..." : "Select heat number from selected ER (linked to RM IC)"}
                        >
                          <select
                            className="ric-form-select"
                            value={lotHeat.compositeKey || ''}
                            onChange={(e) => {
                              const compositeKey = e.target.value;
                              if (compositeKey) {
                                // Parse composite key: "heatNumber|manufacturer"
                                const [heatNum, manufacturer] = compositeKey.split('|');
                                const selectedHeat = processHeatNumbers.find(
                                  h => h.heatNumber === heatNum && h.manufacturer === manufacturer
                                );
                                if (selectedHeat) {
                                  handleProcessManufacturerHeatChange(
                                    lotHeat.id,
                                    `${selectedHeat.manufacturer} - ${heatNum}`,
                                    heatNum,
                                    selectedHeat.manufacturer,
                                    selectedHeat.qtyAccepted || 0,
                                    selectedHeat.weightAcceptedMt || 0,
                                    compositeKey
                                  );
                                }
                              } else {
                                handleProcessManufacturerHeatChange(lotHeat.id, '', '', '', 0, 0, '');
                              }
                            }}
                            disabled={loadingHeats || processHeatNumbers.length === 0}
                          >
                            <option value="">
                              {loadingHeats ? "Loading..." : processHeatNumbers.length === 0 ? "Select RM IC first" : "Select Heat Number"}
                            </option>
                            {processHeatNumbers.map((heat) => {
                              const compositeKey = `${heat.heatNumber}|${heat.manufacturer}`;
                              return (
                                <option key={compositeKey} value={compositeKey}>
                                  {heat.heatNumber} - ({heat.manufacturer})
                                </option>
                              );
                            })}
                          </select>
                        </FormField>

                        {/* Offered Quantity */}
                        {(() => {
                          const heatSummary = lotHeat.heatNumber ? heatSummaryData.find(h => h.heatNo === lotHeat.heatNumber) : null;
                          const offeredQty = parseInt(lotHeat.offeredQty) || 0;

                          // Format accepted weight for display in label
                          const acceptedWeightDisplay = (heatSummary && heatSummary.acceptedMt != null) ? `${(parseFloat(heatSummary.acceptedMt) || 0).toFixed(3)} MT` : '';

                          // Calculate available balance for THIS lot
                          // Available = Max ERC - Offered Earlier - (Total offered by OTHER lots using same heat)
                          let availableBalance = 0;
                          if (heatSummary) {
                            // Calculate total offered by OTHER lots using the same heat number
                            const otherLotsOffered = formData.process_lot_heat_mapping
                              .filter(lot => lot.heatNumber === lotHeat.heatNumber && lot.id !== lotHeat.id)
                              .reduce((sum, lot) => sum + (parseInt(lot.offeredQty) || 0), 0);

                            availableBalance = heatSummary.maxErc - (heatSummary.offeredEarlier || 0) - otherLotsOffered;
                          }

                          const exceedsBalance = !isViewMode && heatSummary && (offeredQty > availableBalance);

                          return (
                            <FormField
                              label={`Declared Quantity of Lot in Nos. ${acceptedWeightDisplay ? `(Accepted: ${acceptedWeightDisplay})` : ''}`}
                              name={`process_offered_qty_${lotHeat.id}`}
                              required
                              hint={heatSummary ? `Available Future Balance: ${availableBalance} ERCs` : "Select heat number first"}
                              errors={exceedsBalance ? { [`process_offered_qty_${lotHeat.id}`]: `Exceeds available balance by ${offeredQty - availableBalance} ERCs` } : {}}
                            >
                              <input
                                type="number"
                                className={`ric-form-input ${exceedsBalance ? 'ric-form-input--error' : ''}`}
                                value={lotHeat.offeredQty}
                                onChange={(e) => handleProcessOfferedQtyChange(lotHeat.id, e.target.value)}
                                min="0"
                                max={heatSummary ? availableBalance : (lotHeat.maxQty || undefined)}
                                placeholder="Enter quantity in Number"
                                disabled={isViewMode || !lotHeat.heatNumber}
                                style={exceedsBalance ? { borderColor: '#ef4444', backgroundColor: '#fef2f2' } : (isViewMode ? { backgroundColor: '#f9fafb', cursor: 'default' } : {})}
                              />
                            </FormField>
                          );
                        })()}

                        {/* Tentative Start Date */}
                        <FormField
                          label="Tentative Date of Start of Production"
                          name={`process_tentative_start_date_${lotHeat.id}`}
                          // required // Commented out to allow infinite range
                          hint="Expected date when production will start for this lot"
                        >
                          <input
                            type="date"
                            className="ric-form-input"
                            value={lotHeat.tentativeStartDate}
                            onChange={(e) => handleProcessTentativeStartDateChange(lotHeat.id, e.target.value)}
                          // min={new Date().toISOString().split('T')[0]} // Removed to allow infinite range
                          />
                        </FormField>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="ric-form-grid">
                </div>
              </>
            )}

            {/* ============ FINAL STAGE FIELDS ============ */}
            {formData.type_of_call === 'Final' && (
              <>
                <SectionHeader
                  title="ERC Final Inspection Details"
                  subtitle="Select lots and enter final quantity details"
                />

                <div className="ric-form-grid">
                  {/* STEP 1: RM IC Numbers - Only for FRESH calls */}
                  {formData.call_category !== 'DIVERTED' && (
                    <FormField
                      label="RM IC Numbers"
                      name="final_rm_ic_numbers"
                      required
                      hint={completeDiversionSrNos.has(formData.po_serial_no) ? "RM ICs not available — material for this PO SR no has been completely diverted" : "Certificate numbers from completed RM ICs (ER prefix)"}
                    >
                      {loadingRmIcsForFinal ? (
                        <div style={{ padding: '12px', color: '#666', fontStyle: 'italic' }}>
                          Loading RM IC certificates...
                        </div>
                      ) : (
                        <MultiSelectDropdown
                          options={rmIcNumbersForFinal.map(item => {
                            const certNo = (item && typeof item === 'object') ? item.certificateNo : (item || '');
                            const createdAt = (item && typeof item === 'object') ? item.createdAt : null;
                            const displayLabel = createdAt ? `${certNo} (${formatDate(createdAt)})` : certNo;
                            return {
                              value: certNo,
                              label: displayLabel
                            };
                          })}
                          selectedValues={formData.final_rm_ic_numbers}
                          onChange={(selectedValues) => {
                            setFormData(prev => ({
                              ...prev,
                              final_rm_ic_numbers: selectedValues,
                              final_process_ic_numbers: [], // Reset downstream selections
                              final_lot_numbers: []
                            }));
                          }}
                          placeholder={completeDiversionSrNos.has(formData.po_serial_no) ? "Not available — material completely diverted" : "Select RM IC Certificate Number"}
                          disabled={isViewMode || completeDiversionSrNos.has(formData.po_serial_no)}
                        />
                      )}
                      {formData.final_rm_ic_numbers.length > 0 && (
                        <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                          Selected: {formData.final_rm_ic_numbers.join(', ')}
                        </div>
                      )}
                    </FormField>
                  )}

                  {/* STEP 2: Process IC Numbers - Second Dropdown (Multi-Select) */}
                  <FormField
                    label="Process IC Numbers"
                    name="final_process_ic_numbers"
                    required
                    hint={formData.call_category === 'DIVERTED' ? "Diverted Process IC certificates for this PO Serial" : "Certificate numbers from completed Process ICs (EP prefix)"}
                    fullWidth={formData.call_category === 'DIVERTED'}
                  >
                    {loadingProcessIcs ? (
                      <div style={{ padding: '12px', color: '#666', fontStyle: 'italic' }}>
                        Loading Process IC certificates...
                      </div>
                    ) : (
                      <MultiSelectDropdown
                        options={processIcCertificates.map(item => {
                          const certNo = (item && typeof item === 'object') ? item.certificateNo : (item || '');
                          const createdAt = (item && typeof item === 'object') ? item.createdAt : null;
                          const displayLabel = createdAt ? `${certNo} (${formatDate(createdAt)})` : certNo;
                          return {
                            value: certNo,
                            label: displayLabel
                          };
                        })}
                        selectedValues={formData.final_process_ic_numbers}
                        onChange={async (selectedValues) => {
                          setFormData(prev => ({
                            ...prev,
                            final_process_ic_numbers: selectedValues,
                            final_lot_numbers: [] // Reset downstream selections
                          }));

                          // In Diverted mode: when a Process IC is chosen, auto-populate the RM IC if empty
                          if (formData.call_category === 'DIVERTED' && selectedValues && selectedValues.length > 0) {
                            try {
                              const rmRes = await inspectionCallService.getRmIcsByProcessCertificate(selectedValues[0]);
                              if (rmRes && rmRes.data && rmRes.data.length > 0) {
                                setFormData(prev => ({
                                  ...prev,
                                  final_rm_ic_numbers: prev.final_rm_ic_numbers.length === 0 ? rmRes.data : prev.final_rm_ic_numbers
                                }));
                              }
                            } catch (e) {
                              console.warn('Could not auto-fetch RM IC for diverted Process IC:', e);
                            }
                          }
                        }}
                        placeholder={formData.call_category !== 'DIVERTED' && formData.final_rm_ic_numbers.length === 0 ? "Select RM IC first" : "Select Process IC Certificate Number"}
                        disabled={isViewMode || (formData.call_category !== 'DIVERTED' && formData.final_rm_ic_numbers.length === 0)}
                      />
                    )}
                    {formData.final_process_ic_numbers.length > 0 && (
                      <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                        Selected: {formData.final_process_ic_numbers.join(', ')}
                      </div>
                    )}
                  </FormField>

                  {/* STEP 3: Lot Numbers - Third Dropdown (Multi-Select) */}
                  <FormField
                    label="Lot No."
                    name="final_lot_numbers"
                    required
                    hint={formData.call_category === 'DIVERTED' ? "Lot numbers from selected Process IC" : "Lot numbers from selected RM IC and Process IC"}
                    fullWidth
                  >
                    {loadingLotsForFinal ? (
                      <div style={{ padding: '12px', color: '#666', fontStyle: 'italic' }}>
                        Loading lot numbers...
                      </div>
                    ) : (
                      <MultiSelectDropdown
                        options={lotNumbersForFinal.map(lot => ({
                          value: lot,
                          label: lot
                        }))}
                        selectedValues={formData.final_lot_numbers}
                        onChange={(selectedValues) => {
                          setFormData(prev => ({ ...prev, final_lot_numbers: selectedValues }));
                        }}
                        placeholder={
                          formData.call_category !== 'DIVERTED' && formData.final_rm_ic_numbers.length === 0
                            ? "Select RM IC first"
                            : formData.final_process_ic_numbers.length === 0
                              ? "Select Process IC first"
                              : "Select Lot Numbers"
                        }
                        disabled={
                          isViewMode ||
                          (formData.call_category !== 'DIVERTED' && formData.final_rm_ic_numbers.length === 0) ||
                          formData.final_process_ic_numbers.length === 0
                        }
                      />
                    )}
                    {formData.final_lot_numbers.length > 0 && (
                      <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                        Selected: {formData.final_lot_numbers.join(', ')}
                      </div>
                    )}
                  </FormField>
                </div>

                {/* STEP 4: Lot Details Table */}
                {formData.final_lots_data.length > 0 && (
                  <div style={{ marginTop: '24px' }}>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      marginBottom: '16px',
                      color: '#1f2937',
                      borderBottom: '2px solid #e5e7eb',
                      paddingBottom: '8px'
                    }}>
                      Lot Details ({formData.final_lots_data.length} lot{formData.final_lots_data.length > 1 ? 's' : ''} selected)
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                      <table className="ric-table" style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f3f4f6' }}>
                            <th style={{ padding: '12px', border: '1px solid #e5e7eb', textAlign: 'left' }}>Lot No.</th>
                            <th style={{ padding: '12px', border: '1px solid #e5e7eb', textAlign: 'left' }}>Heat No.</th>
                            <th style={{ padding: '12px', border: '1px solid #e5e7eb', textAlign: 'left' }}>Qty Accepted in Process (Auto)</th>
                            <th style={{ padding: '12px', border: '1px solid #e5e7eb', textAlign: 'left' }}>Called Offered Earlier</th>
                            <th style={{ padding: '12px', border: '1px solid #e5e7eb', textAlign: 'left' }}>Future Balance</th>
                            <th style={{ padding: '12px', border: '1px solid #e5e7eb', textAlign: 'left' }}>Qty Offered for Inspection</th>
                            <th style={{ padding: '12px', border: '1px solid #e5e7eb', textAlign: 'left' }}>No. of Bags</th>
                            {!isViewMode && <th style={{ padding: '12px', border: '1px solid #e5e7eb', textAlign: 'center' }}>Action</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {formData.final_lots_data.map((lot) => {
                            const totalAvailable = Math.max(0, (lot.acceptedQtyProcess || 0) - (lot.offeredEarlier || 0));
                            const currentOffered = parseInt(lot.offeredQty) || 0;
                            const dynamicBalance = isViewMode ? 0 : Math.max(0, totalAvailable - currentOffered);
                            const isQtyInvalid = !isViewMode && (currentOffered > totalAvailable);
                            const minBagsRequired = Math.ceil(currentOffered / 50);
                            const isBagsInvalid = !isViewMode && (lot.noOfBags > 0 && lot.noOfBags < minBagsRequired);

                            return (
                              <tr key={lot.lotNumber}>
                                <td style={{ padding: '12px', border: '1px solid #e5e7eb' }}>{lot.lotNumber}</td>
                                <td style={{ padding: '12px', border: '1px solid #e5e7eb' }}>{lot.heatNo}</td>
                                <td style={{ padding: '12px', border: '1px solid #e5e7eb' }}>{lot.acceptedQtyProcess}</td>
                                <td style={{ padding: '12px', border: '1px solid #e5e7eb' }}>{lot.offeredEarlier || 0}</td>
                                <td style={{ padding: '12px', border: '1px solid #e5e7eb', fontWeight: 'bold', color: '#059669' }}>
                                  {dynamicBalance}
                                </td>
                                <td style={{ padding: '12px', border: '1px solid #e5e7eb' }}>
                                  <input
                                    type="number"
                                    className={`ric-form-input ${isQtyInvalid ? 'error' : ''}`}
                                    value={lot.offeredQty}
                                    onChange={(e) => handleFinalLotDataChange(lot.lotNumber, 'offeredQty', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                                    placeholder="Quantity"
                                    max={totalAvailable}
                                    disabled={isViewMode}
                                    style={isQtyInvalid ? { borderColor: '#ef4444', backgroundColor: '#fef2f2' } : (isViewMode ? { backgroundColor: '#f9fafb', cursor: 'default' } : {})}
                                  />
                                  {isQtyInvalid && (
                                    <div style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px' }}>
                                      Cannot exceed available balance ({totalAvailable})
                                    </div>
                                  )}
                                </td>
                                <td style={{ padding: '12px', border: '1px solid #e5e7eb' }}>
                                  <input
                                    type="number"
                                    className={`ric-form-input ${isBagsInvalid ? 'error' : ''}`}
                                    value={lot.noOfBags}
                                    onChange={(e) => handleFinalLotDataChange(lot.lotNumber, 'noOfBags', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                                    placeholder="Bags"
                                    disabled={isViewMode}
                                    style={isBagsInvalid ? { borderColor: '#ef4444', backgroundColor: '#fef2f2' } : (isViewMode ? { backgroundColor: '#f9fafb', cursor: 'default' } : {})}
                                  />
                                  {isBagsInvalid && (
                                    <div style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px' }}>
                                      Min {minBagsRequired} bags required (Qty/50)
                                    </div>
                                  )}
                                </td>
                                {!isViewMode && (
                                  <td style={{ padding: '12px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const newLots = formData.final_lot_numbers.filter(l => l !== lot.lotNumber);
                                        setFormData(prev => ({ ...prev, final_lot_numbers: newLots }));
                                      }}
                                      style={{
                                        padding: '4px 8px',
                                        backgroundColor: '#fee2e2',
                                        color: '#ef4444',
                                        border: '1px solid #fecaca',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px'
                                      }}
                                    >
                                      Remove
                                    </button>
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="ric-form-grid">
                  <FormField
                    label="Qunantity (No. of ERC)"
                    name="final_erc_qty"
                    hint="Sum of offered quantity from all lots"
                  >
                    <input
                      type="number"
                      name="final_erc_qty"
                      className="ric-form-input ric-form-input--disabled"
                      value={formData.final_erc_qty}
                      disabled
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                    />
                  </FormField>

                  <FormField
                    label="Total Qty"
                    name="final_total_qty"
                    hint="Auto-calculated from Lot Details"
                  >
                    <input
                      type="text"
                      className="ric-form-input ric-form-input--disabled"
                      value={formData.final_total_qty}
                      disabled
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                      placeholder="Auto-calculated"
                    />
                  </FormField>

                  <FormField
                    label="No of HDPE Bags"
                    name="final_hdpe_bags"
                    hint="Sum of bags from all lots"
                  >
                    <input
                      type="number"
                      name="final_hdpe_bags"
                      className="ric-form-input ric-form-input--disabled"
                      value={formData.final_hdpe_bags}
                      disabled
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                      placeholder="Auto-calculated"
                    />
                  </FormField>
                </div>
              </>
            )}

            {/* ============ PLACE OF INSPECTION ============ */}
            <SectionHeader title="Place of Inspection" subtitle="Select unit for inspection" />

            <div className="ric-form-grid">
              {/* Company Name - Dropdown */}
              <FormField label="Place of Inspection - Company Name" name="company_name" required errors={errors}>
                <select
                  className="ric-form-select"
                  value={formData.company_name}
                  onChange={(e) => {
                    const selectedCompanyName = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      company_name: selectedCompanyName,
                      unit_name: '',
                      unit_address: ''
                    }));
                    setPoiCode('');
                  }}
                  disabled={loadingCompanies}
                >
                  <option value="">
                    {loadingCompanies ? 'Loading companies...' : '-- Select Company --'}
                  </option>
                  {formData.company_name && !companies.includes(formData.company_name) && (
                    <option key="prefilled-company" value={formData.company_name}>
                      {formData.company_name}
                    </option>
                  )}
                  {companies.map((companyName, index) => (
                    <option key={index} value={companyName}>
                      {companyName}
                    </option>
                  ))}
                </select>
              </FormField>

              {/* Unit Name - Dropdown for all call types */}
              <FormField
                label="place of Inspection - Unit Name"
                name="unit_name"
                required
                hint="DropDown (based upon the selection of Company name)"
              >
                <select
                  className="ric-form-select"
                  value={formData.unit_name}
                  onChange={(e) => {
                    const selectedUnitName = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      unit_name: selectedUnitName,
                      unit_address: ''
                    }));
                    setPoiCode('');
                  }}
                  disabled={loadingUnits || !formData.company_name}
                >
                  <option value="">
                    {loadingUnits ? 'Loading units...' :
                      !formData.company_name ? 'Select company first' :
                        'Select Unit'}
                  </option>
                  {formData.unit_name && !units.includes(formData.unit_name) && (
                    <option key="prefilled-unit" value={formData.unit_name}>
                      {formData.unit_name}
                    </option>
                  )}
                  {units.map((unitName, index) => (
                    <option key={index} value={unitName}>
                      {unitName}
                    </option>
                  ))}
                </select>
              </FormField>

              {/* Unit Address - Auto Fetch for all call types */}
              <FormField label="place of Inspection - Unit Address" name="unit_address" hint="Auto Fetch" fullWidth>
                <input
                  type="text"
                  className="ric-form-input ric-form-input--disabled"
                  value={loadingUnitDetails ? 'Loading address...' : formData.unit_address}
                  disabled
                />
              </FormField>
            </div>

            {/* Payment Block Warning */}
            {isPoiBlocked && (
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                background: '#fff7ed',
                border: '1px solid #fed7aa',
                borderLeft: '4px solid #ea580c',
                borderRadius: '6px',
                padding: '12px 16px',
                margin: '8px 0 4px',
                color: '#9a3412',
                fontSize: '13.5px',
                lineHeight: '1.5'
              }}>
                <span style={{ fontSize: '16px', marginTop: '1px' }}>⚠️</span>
                <div>
                  <strong>Call Raising Blocked</strong>
                  <div style={{ marginTop: '2px' }}>{poiBlockMessage}</div>
                </div>
              </div>
            )}

            <SectionHeader title="Additional Information" />

            <div className="ric-form-grid">
              <FormField label="Remarks" name="remarks" fullWidth>
                <textarea
                  name="remarks"
                  className="ric-form-textarea"
                  value={formData.remarks}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Enter any additional remarks..."
                />
              </FormField>
            </div>
          </>
        )}
      </fieldset>

      {formData.type_of_call && (
        <div className="ric-form-actions" style={isViewMode ? { justifyContent: 'center' } : {}}>
          {!isViewMode ? (
            <>
              <button
                type="button"
                className="btn btn-outline"
                onClick={handleReset}
                disabled={isLoading}
              >
                Reset Form
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={isLoading || !formData.type_of_call || isPoiBlocked}
                title={isPoiBlocked ? 'Call raising is blocked due to pending cancellation payment' : ''}
              >
                {isLoading ? (isModifyMode ? 'Updating...' : 'Submitting...') : (isModifyMode ? 'Update Inspection Call' : 'Submit Inspection Call')}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn btn-outline"
              onClick={onSubmit}
              style={{ minWidth: '120px' }}
            >
              Close
            </button>
          )}
        </div>
      )}

      {/* Clip Type Warning Modal */}
      {showErcWarning && (
        <div
          onClick={() => {
            setShowErcWarning(false);
            setPendingErcType('');
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.35)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              maxWidth: '440px',
              width: '100%',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Modal Header */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #f1f5f9',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
                Warning
              </h3>
              <button
                onClick={() => {
                  setShowErcWarning(false);
                  setPendingErcType('');
                }}
                style={{
                  border: 'none',
                  background: 'none',
                  fontSize: '16px',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  width: '28px',
                  height: '28px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f1f5f9';
                  e.currentTarget.style.color = '#475569';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#94a3b8';
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px 20px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  background: 'rgba(254, 243, 199, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px',
                  boxShadow: '0 8px 16px rgba(251, 191, 36, 0.12)',
                  border: '1px solid rgba(251, 191, 36, 0.3)'
                }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                    <line x1="12" y1="9" x2="12" y2="13"></line>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                </div>
                <p style={{ fontSize: '15px', color: '#334155', lineHeight: '1.6', margin: 0, fontWeight: '500' }}>
                  you have selected <span style={{ color: '#be123c', fontWeight: '700', textDecoration: 'underline' }}>{pendingErcType}</span> clip , are you sure to contnue
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 20px',
              borderTop: '1px solid #f1f5f9',
              background: '#f8fafc',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                className="btn btn-outline"
                onClick={() => {
                  setShowErcWarning(false);
                  setPendingErcType('');
                }}
                style={{
                  borderRadius: '8px',
                  padding: '8px 16px',
                  fontWeight: '600',
                  border: '1px solid #cbd5e1',
                  color: '#475569',
                  backgroundColor: '#ffffff',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'all 0.15s ease',
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setFormData(prev => ({ ...prev, type_of_erc: pendingErcType }));
                  setShowErcWarning(false);
                  setPendingErcType('');
                }}
                style={{
                  borderRadius: '8px',
                  padding: '8px 20px',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, #e11d48, #be123c)',
                  color: '#ffffff',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  boxShadow: '0 4px 12px rgba(225, 29, 72, 0.25)',
                  transition: 'all 0.15s ease',
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Notification */}
      <Notification
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: '', type: 'error' })}
        autoClose={true}
        autoCloseDelay={5000}
      />
    </div>
  );
};

export default RaiseInspectionCallForm;
